import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "../api/client";
import { useAuth } from "../auth/AuthContext";
import { useMeInfo, useIsAdmin, useGremiumRole, roleSatisfies } from "../hooks/useMeInfo";
import { Badge, Modal, Spinner, Alert, FormGroup } from "../components/UI";
import PersoenlicheNotizen from "../components/PersoenlicheNotizen";
import SitzungsgeldWidget from "../components/SitzungsgeldWidget";
import dayjs from "dayjs";
import "dayjs/locale/de";
dayjs.locale("de");

const TABS = ["Tagesordnung", "Anträge", "Gäste", "Ladung", "Protokoll", "Beschlüsse", "Zusammenfassung", "Verlauf"];

export default function SitzungDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [sitzung, setSitzung] = useState(null);
  const [beschlussfaehigkeit, setBeschlussfaehigkeit] = useState(null);
  const [nachfolgerSitzung, setNachfolgerSitzung] = useState(null);
  const [showWiederholung, setShowWiederholung] = useState(false);
  const [wdhForm, setWdhForm] = useState({ modus: "minuten", wert: 15, begruendung: "" });
  const [wdhSubmitting, setWdhSubmitting] = useState(false);
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [showAbsage, setShowAbsage] = useState(false);
  const [genehmigungen, setGenehmigungen] = useState([]);
  const [showAblehnungDetail, setShowAblehnungDetail] = useState(null);

  const loadGenehmigungen = () => {
    api.getGenehmigungen(id).then(setGenehmigungen).catch(() => {});
  };
  const [absageGrund, setAbsageGrund] = useState("");

  const load = () => {
    api.getSitzung(id).then(setSitzung).catch(() => navigate("/sitzungen")).finally(() => setLoading(false));
    api.getBeschlussfaehigkeit(id).then(setBeschlussfaehigkeit).catch(() => setBeschlussfaehigkeit(null));
    // Phase B: Nachfolger laden (wenn diese Sitzung beschlussunfaehig geschlossen wurde, gibt's eine Wiederholung)
    api.getNachfolgerSitzung(id).then(setNachfolgerSitzung).catch(() => setNachfolgerSitzung(null));
  };
  useEffect(() => { load(); loadGenehmigungen(); }, [id]);
  // Permission-Hooks (müssen vor early returns aufgerufen werden!)
  const { info: meInfo } = useMeInfo();
  const isAdmin = useIsAdmin();
  const role = useGremiumRole(sitzung?.gremium_id);
  const canVorsitz        = isAdmin || roleSatisfies(role, "vorsitz");
  const canSchriftfuehrer = isAdmin || roleSatisfies(role, "schriftfuehrer");
  const myEmail = meInfo?.email || null;
  const showVerlauf = canSchriftfuehrer || (sitzung?.verlauf_fuer_mitglieder === true);



  if (loading) return <Spinner />;
  if (!sitzung) return null;
  return (
    <div>
      <div className="page-header">
        <div>
          <div className="breadcrumb">
            <span onClick={() => navigate("/sitzungen")} style={{ cursor: "pointer" }}>Sitzungen</span> /
          </div>
          <h2>{sitzung.titel}</h2>
        </div>
        <div className="page-actions">
          <Badge status={sitzung.status} />
          {canVorsitz && (<button className="btn btn-ghost btn-sm" onClick={() => window.open(`/projektor/${id}/steuerung`, "_blank")}>🎬 Projektor</button>)}
          {canVorsitz && sitzung.status !== "durchgefuehrt" && sitzung.status !== "abgesagt" && sitzung.status !== "geschlossen_beschlussunfaehig" && (
            <button className="btn btn-ghost btn-sm" style={{ marginRight: 8 }} onClick={() => navigate(`/sitzungen/${id}/bearbeiten`)}>✎ Bearbeiten</button>
          )}
          {canVorsitz && sitzung.status !== "durchgefuehrt" && sitzung.status !== "abgesagt" && sitzung.status !== "geschlossen_beschlussunfaehig" && (
            <>
            <button className="btn btn-danger btn-sm" style={{ marginRight: 8 }} onClick={() => setShowAbsage(true)}>✕ Absagen</button>
            <button className="btn btn-secondary" onClick={async () => {
              if (!window.confirm("Sitzung wirklich abschließen?\n\n• Status wird auf \"Durchgeführt\" gesetzt\n• QR-Check-in wird sofort gesperrt\n• Diese Aktion kann nicht rückgängig gemacht werden")) return;
              await api.abschliessenSitzung(id); load();
              setSuccess("Sitzung als durchgeführt markiert.");
            }}>✓ Abschließen</button>
            </>
          )}
        </div>
      </div>

      <div className="content">
              {showAblehnungDetail && (
        <Modal title="Ablehnung — Details" onClose={() => setShowAblehnungDetail(null)}
          footer={<button className="btn btn-secondary" onClick={() => setShowAblehnungDetail(null)}>Schließen</button>}>
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: "var(--ink-soft)", marginBottom: 4 }}>Person</div>
            <div>{showAblehnungDetail.person_name} ({showAblehnungDetail.person_email})</div>
          </div>
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: "var(--ink-soft)", marginBottom: 4 }}>Abgelehnt am</div>
            <div>{dayjs(showAblehnungDetail.abgelehnt_am).format("DD.MM.YYYY HH:mm")} Uhr</div>
          </div>
          <div>
            <div style={{ fontSize: 12, fontWeight: 600, color: "var(--ink-soft)", marginBottom: 4 }}>Begründung</div>
            <div style={{ background: "var(--danger-bg)", border: "1px solid #e8c9c7", borderRadius: 6, padding: "12px 14px", color: "var(--danger)" }}>
              {showAblehnungDetail.ablehnungsgrund}
            </div>
          </div>
        </Modal>
      )}
      {showAbsage && (
        <Modal title="Sitzung absagen" onClose={() => { setShowAbsage(false); setAbsageGrund(""); }}
          footer={<>
            <button className="btn btn-secondary" onClick={() => { setShowAbsage(false); setAbsageGrund(""); }}>Abbrechen</button>
            <button className="btn btn-danger" onClick={async () => {
              if (!absageGrund.trim()) { setError("Bitte Begründung angeben."); return; }
              try {
                await api.absagenSitzung(sitzung.id, { begruendung: absageGrund });
                setShowAbsage(false); setAbsageGrund(""); setSuccess("Sitzung abgesagt."); load();
              } catch { setError("Fehler."); }
            }}>Absagen</button>
          </>}>
          <div style={{ color: "var(--danger)", fontWeight: 600, marginBottom: 12 }}>⚠ Sitzung wirklich absagen?</div>
          <div style={{ fontSize: 12, fontWeight: 600, color: "var(--ink-soft)", marginBottom: 4 }}>Begründung *</div>
          <textarea className="form-control" rows={3} value={absageGrund}
            placeholder="z. B. Terminkonflikt…"
            onChange={e => setAbsageGrund(e.target.value)} />
        </Modal>
      )}
      {error && <Alert type="error">{error}</Alert>}
        {success && <Alert type="success">{success}</Alert>}

        {/* Meta */}
        <div className="card mb-24">
          <div className="card-body" style={{ padding: "16px 20px" }}>
            <div style={{ display: "flex", gap: 40 }}>
              <div><div className="form-label">Aktenzeichen</div><strong style={{ fontFamily: "var(--mono)", color: "var(--red)" }}>{sitzung.aktenzeichen || "—"}</strong></div>
              <div><div className="form-label">Gremium</div><strong>{sitzung.gremium?.name}</strong></div>
              <div><div className="form-label">Datum</div><strong>{dayjs(sitzung.datum).format("dddd, DD.MM.YYYY")}</strong></div>
              <div><div className="form-label">Uhrzeit</div><strong>{dayjs(sitzung.datum).format("HH:mm")} Uhr</strong></div>
              <div><div className="form-label">Ort</div><strong>{sitzung.ort || "—"}</strong></div>
              {sitzung.antraege_frist && <div><div className="form-label">Antragsfrist</div><strong style={{ color: dayjs().isAfter(dayjs(sitzung.antraege_frist)) ? "var(--red)" : "inherit" }}>{dayjs(sitzung.antraege_frist).format("DD.MM.YYYY HH:mm")} Uhr</strong></div>}
              
              {sitzung.ladung_versendet_am && (
                <div><div className="form-label">Ladung versandt</div>
                  <strong>{dayjs(sitzung.ladung_versendet_am).format("DD.MM.YYYY HH:mm")}</strong></div>
              )}
            </div>
          </div>
        </div>

        {/* Phase B: Vorgänger/Nachfolger-Hinweise */}
        {sitzung.ist_wiederholungssitzung && sitzung.vorgaenger_sitzung_id && (
          <div className="card mb-24"
               style={{ background: "var(--warn-bg)", border: "1px solid var(--warn)", padding: "10px 16px" }}>
            <div style={{ fontSize: 13, color: "var(--warn)" }}>
              ⟳ <strong>Wiederholungssitzung</strong> — diese Sitzung ist eine Wiederholung der wegen Beschlussunfähigkeit geschlossenen{" "}
              <a href={"#/sitzungen/" + sitzung.vorgaenger_sitzung_id}
                 onClick={(e) => { e.preventDefault(); navigate("/sitzungen/" + sitzung.vorgaenger_sitzung_id); }}
                 style={{ color: "var(--warn)", textDecoration: "underline" }}>Vorgänger-Sitzung</a>.
              In dieser Sitzung gelten keine Mindest-Quoten für die Beschlussfähigkeit.
            </div>
          </div>
        )}
        {nachfolgerSitzung && (
          <div className="card mb-24"
               style={{ background: "var(--warn-bg)", border: "1px solid var(--warn)", padding: "10px 16px" }}>
            <div style={{ fontSize: 13, color: "var(--warn)" }}>
              ⟳ Diese Sitzung wurde wegen Beschlussunfähigkeit geschlossen. Eine{" "}
              <a href={"#/sitzungen/" + nachfolgerSitzung.id}
                 onClick={(e) => { e.preventDefault(); navigate("/sitzungen/" + nachfolgerSitzung.id); }}
                 style={{ color: "var(--warn)", textDecoration: "underline" }}>
                Wiederholungssitzung (UEM-...-W)
              </a>{" "}wurde einberufen.
            </div>
          </div>
        )}

        {/* Phase B: Beschlussfaehigkeits-Karte */}
        {beschlussfaehigkeit && (() => {
          const bf = beschlussfaehigkeit;
          let bg, border, icon, headline;
          if (bf.ist_wiederholungssitzung) {
            bg = "var(--paper)"; border = "var(--ink-faint)"; icon = "i"; headline = "Wiederholungssitzung - kein Quorum erforderlich";
          } else if (bf.quote_gefordert === 0) {
            bg = "var(--paper)"; border = "var(--ink-faint)"; icon = "i"; headline = "Keine Beschluss-TOPs auf der Tagesordnung";
          } else if (bf.beschlussfaehig) {
            bg = "var(--ok-bg)"; border = "var(--ok)"; icon = "OK"; headline = "Beschlussfaehig";
          } else {
            bg = "var(--danger-bg)"; border = "var(--danger)"; icon = "X"; headline = "NICHT beschlussfaehig";
          }
          return (
            <div className="card mb-24" style={{ background: bg, border: "1px solid " + border, padding: 0 }}>
              <div style={{ padding: "12px 20px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <div style={{ fontSize: 22, fontWeight: 700, color: border, lineHeight: 1, minWidth: 32, textAlign: "center" }}>{icon}</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: 15, color: border }}>
                      Beschlussfaehigkeit: {headline}
                    </div>
                    {bf.quote_gefordert > 0 && !bf.ist_wiederholungssitzung && (
                      <div style={{ fontSize: 13, color: "var(--ink-soft)", marginTop: 4 }}>
                        <strong>{bf.ist}</strong> von <strong>{bf.soll}</strong> stimmberechtigten anwesend ({bf.prozent}%)
                        - erforderlich: <strong>{bf.quote_gefordert}%</strong>
                        {bf.quote_quelle && <span style={{ color: "var(--ink-soft)" }}> (durch "{bf.quote_quelle}")</span>}
                      </div>
                    )}
                    {bf.hinweis && <div style={{ fontSize: 12, color: "var(--ink-faint)", marginTop: 4 }}>{bf.hinweis}</div>}
                    {bf.beschluss_tops && bf.beschluss_tops.length > 0 && (
                      <details style={{ marginTop: 8, fontSize: 12 }}>
                        <summary style={{ cursor: "pointer", color: "var(--ink-soft)" }}>
                          {bf.beschluss_tops.length} Beschluss-TOP{bf.beschluss_tops.length === 1 ? "" : "s"} mit Quoten
                        </summary>
                        <ul style={{ marginTop: 6, paddingLeft: 20, color: "var(--ink-soft)" }}>
                          {bf.beschluss_tops.map(t => (
                            <li key={t.top_id}>
                              <strong>{t.top_titel}</strong> - {t.typ_bezeichnung} ({t.anwesenheits_quote}% Anwesenheit)
                            </li>
                          ))}
                        </ul>
                      </details>
                    )}
                  </div>
                </div>
                {/* Button: nur wenn nicht beschlussfähig UND keine Wiederholung */}
                {!bf.beschlussfaehig && !bf.ist_wiederholungssitzung
                  && bf.quote_gefordert > 0
                  && sitzung.status === "geplant"
                  && (
                  <div style={{ marginTop: 12, paddingTop: 12, borderTop: "1px solid " + border + "33" }}>
                    <button className="btn btn-secondary"
                      style={{ background: border, color: "white", border: "none", fontSize: 13, padding: "6px 14px" }}
                      onClick={() => setShowWiederholung(true)}>
                      ⟳ Sitzung schließen & Wiederholung einberufen
                    </button>
                  </div>
                )}
              </div>
            </div>
          );
        })()}

        {/* Modal: Wiederholungssitzung einberufen */}
        {showWiederholung && (
          <Modal title="Wiederholungssitzung einberufen"
                 onClose={() => !wdhSubmitting && setShowWiederholung(false)}
                 footer={<>
                   <button className="btn btn-secondary"
                     disabled={wdhSubmitting}
                     onClick={() => setShowWiederholung(false)}>Abbrechen</button>
                   <button className="btn btn-primary"
                     disabled={wdhSubmitting}
                     onClick={async () => {
                       if (!wdhForm.wert || wdhForm.wert < 1) { alert("Bitte Verzögerung angeben"); return; }
                       if (!confirm("Aktuelle Sitzung wird geschlossen und eine neue Sitzung wird angelegt. Fortfahren?")) return;
                       setWdhSubmitting(true);
                       try {
                         const body = {
                           begruendung: wdhForm.begruendung || null,
                           verzoegerung_minuten: wdhForm.modus === "minuten" ? wdhForm.wert : null,
                           verzoegerung_tage: wdhForm.modus === "tage" ? wdhForm.wert : null,
                         };
                         const r = await api.wiederholungEinberufen(id, body);
                         setShowWiederholung(false);
                         if (r?.neue_sitzung_id) {
                           navigate("/sitzungen/" + r.neue_sitzung_id);
                         }
                       } catch (e) {
                         alert("Fehler: " + (e?.message || e));
                       } finally {
                         setWdhSubmitting(false);
                       }
                     }}>
                     {wdhSubmitting ? "Wird verarbeitet…" : "Schließen & Wiederholung anlegen"}
                   </button>
                 </>}>
            <div style={{ marginBottom: 16, color: "var(--ink-soft)", fontSize: 13 }}>
              Die aktuelle Sitzung wird mit Status „Beschlussunfähig geschlossen" markiert.
              Eine neue Sitzung mit gleicher Tagesordnung und allen Anträgen wird angelegt.
              In der Wiederholungssitzung gelten KEINE Mindest-Quoten für die Beschlussfähigkeit.
            </div>
            <FormGroup label="Verzögerung bis zur neuen Sitzung">
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <input className="form-control" type="number" min="1"
                  style={{ width: 100 }}
                  value={wdhForm.wert}
                  onChange={e => setWdhForm(f => ({ ...f, wert: parseInt(e.target.value) || 1 }))} />
                <select className="form-control" style={{ maxWidth: 140 }}
                  value={wdhForm.modus}
                  onChange={e => setWdhForm(f => ({ ...f, modus: e.target.value }))}>
                  <option value="minuten">Minuten</option>
                  <option value="tage">Tage</option>
                </select>
              </div>
              <p style={{ fontSize: 11, color: "var(--ink-faint)", marginTop: 4 }}>
                Standard: 15 Minuten (z.B. für unmittelbar nachfolgende Sitzung)
              </p>
            </FormGroup>
            <FormGroup label="Begründung (optional, erscheint im Protokoll)">
              <textarea className="form-control" rows={3}
                value={wdhForm.begruendung}
                onChange={e => setWdhForm(f => ({ ...f, begruendung: e.target.value }))} />
            </FormGroup>
          </Modal>
        )}

        {/* Tabs */}
        <div style={{ display: "flex", gap: 0, marginBottom: 0, borderBottom: "2px solid var(--border)" }}>
          {TABS.map((t, i) => {
            // i: 0 Tagesordnung, 1 Anträge, 2 Gäste, 3 Ladung, 4 Protokoll, 5 Beschlüsse, 6 Zusammenfassung, 7 Verlauf
            if (i === 2 && !canVorsitz) return null;             // Gäste nur für Vorsitz
            if (i === 7 && !showVerlauf) return null;            // Verlauf via Gremium-Flag
            return (
            <button key={t} onClick={() => setTab(i)}
              style={{
                padding: "10px 20px", border: "none", cursor: "pointer", background: "none",
                fontFamily: "var(--font)", fontSize: 14, fontWeight: tab === i ? 600 : 400,
                color: tab === i ? "var(--red)" : "var(--ink-soft)",
                borderBottom: tab === i ? "2px solid var(--red)" : "2px solid transparent",
                marginBottom: -2,
              }}>
              {t}
            </button>
            );
          })}

        </div>

        <div style={{ marginTop: 24 }}>
          {tab === 0 && <TagesordnungTab sitzung={sitzung} reload={load} setError={setError} />}
        {tab === 1 && <AntraegeTab sitzung={sitzung} reload={load} setError={setError} setSuccess={setSuccess} />}
          {canVorsitz && tab === 2 && <GaesteTab sitzung={sitzung} setError={setError} setSuccess={setSuccess} />}
        {tab === 3 && <LadungTab sitzung={sitzung} reload={load} setError={setError} setSuccess={setSuccess} />}
          {tab === 4 && <ProtokollTab sitzung={sitzung} reload={load} setError={setError} setSuccess={setSuccess} genehmigungen={genehmigungen} loadGenehmigungen={loadGenehmigungen} setShowAblehnungDetail={setShowAblehnungDetail} />}
          {tab === 5 && <BeschluesseTab sitzung={sitzung} reload={load} />}
        {showVerlauf && tab === 7 && <VerlaufTab sitzung={sitzung} />}
        {tab === 6 && <ZusammenfassungTab sitzung={sitzung} reload={load} setError={setError} setSuccess={setSuccess} />}
        </div>

        <SitzungsgeldWidget sitzungId={parseInt(id, 10)} gremiumId={sitzung.gremium_id} />
        <PersoenlicheNotizen objektTyp="sitzung" objektId={parseInt(id, 10)} />
      </div>
    </div>
  );
}

// ─── TAGESORDNUNG TAB ───────────────────────────────────────────────────────
function TagesordnungTab({ sitzung, reload, setError }) {
  const _role = useGremiumRole(sitzung?.gremium_id);
  const _isAdmin = useIsAdmin();
  const canVorsitz = _isAdmin || roleSatisfies(_role, "vorsitz");
  const canSchriftfuehrer = _isAdmin || roleSatisfies(_role, "schriftfuehrer");
  const { info: meInfo } = useMeInfo();
  const myEmail = meInfo?.email || null;

  const [showModal, setShowModal] = useState(false);
  const [abstimmungsTypenList, setAbstimmungsTypenList] = useState([]);
  useEffect(() => {
    api.getAbstimmungsTypen().then(setAbstimmungsTypenList).catch(() => {});
  }, []);
  const [editTop, setEditTop] = useState(null);
  const [form, setForm] = useState({ position: "", titel: "", beschreibung: "", top_typ: "standard", oeffentlich: true, parent_id: null, vorlage_id: null });
  const [vorlagenListe, setVorlagenListe] = useState([]);
  useEffect(() => {
    api.getVorlagen().then(list => setVorlagenListe(list.filter(v => v.status !== "entwurf" && v.status !== "zurueckgezogen"))).catch(() => {});
  }, []);
  const [anlagenMap, setAnlagenMap] = useState({});
  const [expandedAnlagen, setExpandedAnlagen] = useState({});

  // Baum -> flache Liste mit Tiefe + hierarchischer Nummer (z.B. "4.1.2")
  const flatten = (nodes, tiefe = 0, prefix = "") => {
    let out = [];
    (nodes || []).slice()
      .sort((a, b) => (a.position || 0) - (b.position || 0))
      .forEach((node, idx) => {
        const nummer = prefix ? `${prefix}.${idx + 1}` : `${idx + 1}`;
        out.push({ top: node, tiefe, nummer });
        if (node.children && node.children.length) {
          out = out.concat(flatten(node.children, tiefe + 1, nummer));
        }
      });
    return out;
  };

  // Alle TOPs (rekursiv) fuer Anlagen-Vorladen einsammeln
  const alleTopsFlach = (nodes) => {
    let out = [];
    (nodes || []).forEach(n => {
      out.push(n);
      if (n.children) out = out.concat(alleTopsFlach(n.children));
    });
    return out;
  };

  useEffect(() => {
    alleTopsFlach(sitzung.tops || []).forEach(t => {
      api.listAnlagen(t.id).then(list => {
        setAnlagenMap(prev => ({ ...prev, [t.id]: list }));
      }).catch(() => {});
    });
  }, [sitzung.id]);

  const uploadPdf = async (topId, file) => {
    if (!file) return;
    if (file.type !== "application/pdf") { setError("Nur PDF-Dateien"); return; }
    try {
      await api.uploadAnlage(topId, file);
      const list = await api.listAnlagen(topId);
      setAnlagenMap(prev => ({ ...prev, [topId]: list }));
    } catch (e) { setError("Upload fehlgeschlagen: " + (e.detail || e.message || "")); }
  };

  const removeAnlage = async (topId, aid, name) => {
    if (!window.confirm(`Anlage "${name}" loeschen?`)) return;
    await api.deleteAnlage(aid);
    const list = await api.listAnlagen(topId);
    setAnlagenMap(prev => ({ ...prev, [topId]: list }));
  };

  const formatGroesse = (b) => {
    if (!b) return "";
    if (b < 1024) return b + " B";
    if (b < 1024 * 1024) return (b / 1024).toFixed(1) + " KB";
    return (b / 1024 / 1024).toFixed(1) + " MB";
  };

  const oeffnePdf = async (aid) => {
    try {
      const authHeader = window._kcToken ? { Authorization: `Bearer ${window._kcToken}` } : { Authorization: "Bearer " + (localStorage.getItem("internal_token") || "") };
      const res = await fetch(api.anlageDateiUrl(aid), { headers: authHeader });
      if (!res.ok) { setError("PDF konnte nicht geladen werden"); return; }
      const blob = await res.blob();
      window.open(URL.createObjectURL(blob), "_blank");
    } catch (e) { setError("Fehler: " + e.message); }
  };

  // Naechste freie Position innerhalb der Geschwister
  const nextPosFor = (parentId) => {
    let geschwister;
    if (parentId == null) {
      geschwister = sitzung.tops || [];
    } else {
      const flat = alleTopsFlach(sitzung.tops || []);
      const parent = flat.find(t => t.id === parentId);
      geschwister = parent?.children || [];
    }
    return geschwister.length > 0
      ? Math.max(...geschwister.map(t => t.position || 0)) + 1 : 1;
  };

  const openNew = () => {
    setForm({ position: nextPosFor(null), titel: "", beschreibung: "", top_typ: "standard", oeffentlich: true, parent_id: null, abstimmungs_typ_id: null, vorlage_id: null, wahl: null, kandidaten: [] });
    setEditTop(null); setShowModal(true);
  };

  const openNewChild = (parentTop) => {
    setForm({ position: nextPosFor(parentTop.id), titel: "", beschreibung: "", top_typ: "standard", oeffentlich: true, parent_id: parentTop.id, abstimmungs_typ_id: null, vorlage_id: null, wahl: null, kandidaten: [] });
    setEditTop(null); setShowModal(true);
  };

  const openEdit = (top) => {
    setForm({ position: top.position, titel: top.titel, beschreibung: top.beschreibung || "", top_typ: top.top_typ, oeffentlich: top.oeffentlich !== false, parent_id: top.parent_id ?? null, abstimmungs_typ_id: top.abstimmungs_typ_id ?? null, vorlage_id: top.vorlage_id ?? null, wahl: null, kandidaten: [] });
    setEditTop(top); setShowModal(true);
  };

  const save = async () => {
    if (!form.titel || !form.titel.trim()) { setError("Bitte einen Titel eingeben."); return; }
    // Phase C: bei Wahl-TOP Validierung
    if (form.top_typ === "wahl") {
      const validKandidaten = (form.kandidaten || []).filter(k => k.person_name && k.person_name.trim());
      if (validKandidaten.length === 0) { setError("Bitte mindestens einen Kandidaten angeben."); return; }
      if (!form.wahl?.wahlleiter_name?.trim()) { setError("Bitte den Wahlleiter angeben."); return; }
    }
    try {
      // Payload für TOP nur mit TOP-Feldern (Wahl-Felder rausnehmen)
      const { wahl, kandidaten, ...topPayload } = form;
      topPayload.position = Number(form.position);

      let topId;
      if (editTop) {
        await api.updateTOP(sitzung.id, editTop.id, topPayload);
        topId = editTop.id;
      } else {
        const result = await api.createTOP(sitzung.id, topPayload);
        topId = result?.id;
      }

      // Phase C: Wahl anlegen wenn Typ=Wahl und neu erstellt
      if (form.top_typ === "wahl" && topId && !editTop) {
        const validKandidaten = (kandidaten || [])
          .filter(k => k.person_name && k.person_name.trim())
          .map((k, i) => ({
            person_name: k.person_name.trim(),
            person_email: k.person_email?.trim() || null,
            position: i + 1,
          }));
        await api.createWahl(sitzung.id, {
          top_id: topId,
          bezeichnung: form.titel,
          funktion: wahl?.funktion || null,
          wahlart: "mehrheit",
          geheim: wahl?.geheim !== false,
          amtszeit_jahre: wahl?.amtszeit_jahre || null,
          abstimmungs_typ_id: wahl?.abstimmungs_typ_id || null,
          wahlleiter_name: wahl?.wahlleiter_name || null,
          wahlleiter_sub: wahl?.wahlleiter_sub || null,
          kandidaten: validKandidaten,
        });
      }

      setShowModal(false); reload();
    } catch (e) {
      console.error("Speichern fehlgeschlagen:", e);
      setError("Fehler beim Speichern: " + (e?.message || e));
    }
  };

  const del = async (top) => {
    const kinderHinweis = (top.children && top.children.length)
      ? ` Alle ${top.children.length} Unterpunkt(e) werden mitgeloescht!` : "";
    if (!window.confirm(`TOP "${top.titel}" loeschen?${kinderHinweis}`)) return;
    await api.deleteTOP(sitzung.id, top.id); reload();
  };

  const zeilen = flatten(sitzung.tops || []);

  return (
    <>
      <div className="card">
        <div className="card-header">
          <h3>Tagesordnung ({zeilen.length} Punkte)</h3>
          {canVorsitz && (<button className="btn btn-primary btn-sm ml-auto" onClick={openNew}>+ TOP hinzufuegen</button>)}
        </div>
        <div className="card-body">
          {zeilen.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">📋</div>
              <p>Noch keine Tagesordnungspunkte</p>
            </div>
          ) : (
            <ul className="top-list">
              {zeilen.map(({ top, tiefe, nummer }) => (
                <li key={top.id} className="top-item" style={{ marginLeft: tiefe * 32 }}>
                  <div className="top-num" style={tiefe > 0 ? { background: "var(--border)", color: "var(--ink-soft)" } : {}}>{nummer}</div>
                  <div className="top-content">
                    <div className="top-title">{top.titel}
                      {top.vorlage_id && (
                        <Link to={`/vorlagen/${top.vorlage_id}`} onClick={e => e.stopPropagation()}
                          style={{ fontSize: 10, fontWeight: 600, color: "var(--primary)", border: "1px solid var(--primary)", padding: "1px 5px", borderRadius: 3, marginRight: 4, textDecoration: "none" }}>
                          VORLAGE
                        </Link>
                      )}
                      {top.oeffentlich === false && <span style={{ fontSize: 10, fontWeight: 600, color: "var(--warn)", border: "1px solid var(--warn)", padding: "1px 5px", borderRadius: 3, marginRight: 4 }}>NICHT OEFFENTLICH</span>}
                      {top.top_typ !== "standard" && (
                        <span style={{ marginLeft: 8, fontSize: 11,
                          color: top.top_typ === "beschluss" ? "var(--red)" :
                                 top.top_typ === "wahl" ? "#7b1fa2" : "var(--info)",
                          fontWeight: 600 }}>
                          [{top.top_typ === "beschluss" ? "Beschluss" :
                            top.top_typ === "wahl" ? "Wahl" : "Information"}]
                        </span>
                      )}
                    </div>
                    {top.beschreibung && <div className="top-desc">{top.beschreibung}</div>}
                    {top.beschluesse?.length > 0 && (
                      <div style={{ fontSize: 11, color: "var(--red)", marginTop: 4 }}>
                        {top.beschluesse.length} Beschluss/Beschluesse
                      </div>
                    )}
                  </div>
                  <div className="top-actions">
                    <button className="btn btn-ghost btn-icon btn-sm" title="Unterpunkt hinzufuegen" onClick={() => openNewChild(top)}>＋</button>
                    {canVorsitz && (<><button className="btn btn-ghost btn-icon btn-sm" title="Bearbeiten" onClick={() => openEdit(top)}>✎</button>
                    <button className="btn btn-ghost btn-icon btn-sm" title="Loeschen" onClick={() => del(top)}>🗑</button></>)}
                    {/* Anlagen pro TOP */}
                    <div style={{ marginTop: 8, fontSize: 12 }}>
                      <button type="button"
                        onClick={() => setExpandedAnlagen(prev => ({ ...prev, [top.id]: !prev[top.id] }))}
                        style={{ background: "none", border: "none", color: "var(--ink-soft)", cursor: "pointer", padding: 0, fontSize: 12 }}>
                        📎 {(anlagenMap[top.id] || []).length} Anlage(n) {expandedAnlagen[top.id] ? "▾" : "▸"}
                      </button>
                      {expandedAnlagen[top.id] && (
                        <div style={{ marginTop: 6, padding: "8px 12px", background: "var(--paper)", borderRadius: 4 }}>
                          {(anlagenMap[top.id] || []).map(a => (
                            <div key={a.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "4px 0" }}>
                              <span style={{ flex: 1, fontSize: 13 }}>📄 {a.dateiname}
                                <span style={{ color: "var(--ink-faint)", marginLeft: 8, fontSize: 11 }}>{formatGroesse(a.groesse_bytes)}</span>
                              </span>
                              <button className="btn btn-ghost btn-sm" onClick={() => oeffnePdf(a.id)}>Anzeigen</button>
                              <button className="btn btn-danger btn-sm" onClick={() => removeAnlage(top.id, a.id, a.dateiname)}>✕</button>
                            </div>
                          ))}
                          <div style={{ marginTop: 8 }}>
                            <label className="btn btn-secondary btn-sm" style={{ cursor: "pointer" }}>
                              ⬆ PDF hochladen
                              <input type="file" accept="application/pdf" style={{ display: "none" }}
                                onChange={e => { uploadPdf(top.id, e.target.files[0]); e.target.value = ""; }} />
                            </label>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {showModal && (
        <Modal title={editTop ? "TOP bearbeiten" : (form.parent_id ? "Neuer Unterpunkt" : "Neuer Tagesordnungspunkt")}
          onClose={() => setShowModal(false)}
          footer={<>
            <button className="btn btn-secondary" onClick={() => setShowModal(false)}>Abbrechen</button>
            <button className="btn btn-primary" onClick={save}>Speichern</button>
          </>}>
          {form.parent_id && !editTop && (
            <div style={{ fontSize: 12, color: "var(--ink-soft)", marginBottom: 12, padding: "6px 10px", background: "var(--paper)", borderRadius: 4 }}>
              ↳ Unterpunkt zu einem bestehenden TOP
            </div>
          )}
          <div className="form-row">
            <FormGroup label="Position">
              <input className="form-control" type="number" value={form.position}
                onChange={e => setForm(f => ({ ...f, position: e.target.value }))} />
            </FormGroup>
            <FormGroup label="Typ">
              <select className="form-control" value={form.top_typ}
                onChange={e => setForm(f => ({
                  ...f,
                  top_typ: e.target.value,
                  // Beim Wechsel ZU 'beschluss' Default-Typ setzen (einfache_mehrheit)
                  abstimmungs_typ_id: e.target.value === 'beschluss'
                    ? (f.abstimmungs_typ_id || abstimmungsTypenList.find(t => t.schluessel === 'einfache_mehrheit')?.id || null)
                    : null
                }))}>
                <option value="standard">Standard</option>
                <option value="information">Information</option>
                <option value="beschluss">Beschluss</option>
                <option value="wahl">Wahl</option>
              </select>
            </FormGroup>
          </div>
          {form.top_typ === "beschluss" && (
            <FormGroup label="Beschluss-Art (legt Anwesenheits- und Mehrheits-Quote fest)">
              <select className="form-control"
                value={form.abstimmungs_typ_id || ""}
                onChange={e => setForm(f => ({ ...f, abstimmungs_typ_id: e.target.value ? parseInt(e.target.value) : null }))}>
                <option value="">— bitte wählen —</option>
                {abstimmungsTypenList.map(t => (
                  <option key={t.id} value={t.id}>
                    {t.bezeichnung} ({t.anwesenheits_quote_prozent}% Anwes. / {t.mehrheits_quote_prozent}% Mehrheit)
                  </option>
                ))}
              </select>
              {form.abstimmungs_typ_id && (() => {
                const t = abstimmungsTypenList.find(x => x.id === form.abstimmungs_typ_id);
                if (!t) return null;
                return (
                  <p style={{ fontSize: 11, color: "var(--ink-soft)", marginTop: 4 }}>
                    {t.beschreibung}
                  </p>
                );
              })()}
            </FormGroup>
          )}
          {form.top_typ === "wahl" && (
            <div style={{ background: "var(--paper)", padding: 12, borderRadius: 4, marginBottom: 12, border: "1px solid var(--border)" }}>
              <div style={{ fontWeight: 600, marginBottom: 10, fontSize: 13, color: "var(--ink-soft)" }}>Wahl-Konfiguration</div>
              <div className="form-row">
                <FormGroup label="Funktion (z.B. Wehrleiter, Schriftführer)">
                  <input className="form-control"
                    value={form.wahl?.funktion || ""}
                    onChange={e => setForm(f => ({ ...f, wahl: { ...(f.wahl || {}), funktion: e.target.value } }))} />
                </FormGroup>
                <FormGroup label="Amtszeit (Jahre)">
                  <input className="form-control" type="number" min="0" max="20"
                    value={form.wahl?.amtszeit_jahre || ""}
                    onChange={e => setForm(f => ({ ...f, wahl: { ...(f.wahl || {}), amtszeit_jahre: e.target.value ? parseInt(e.target.value) : null } }))} />
                </FormGroup>
              </div>
              <FormGroup label="Wahlleiter">
                <input className="form-control" placeholder="Name des Wahlleiters"
                  value={form.wahl?.wahlleiter_name || ""}
                  onChange={e => setForm(f => ({ ...f, wahl: { ...(f.wahl || {}), wahlleiter_name: e.target.value } }))} />
              </FormGroup>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                <input type="checkbox" id="wahl-geheim"
                  checked={form.wahl?.geheim !== false}
                  onChange={e => setForm(f => ({ ...f, wahl: { ...(f.wahl || {}), geheim: e.target.checked } }))} />
                <label htmlFor="wahl-geheim" style={{ fontSize: 13, cursor: "pointer" }}>Geheime Wahl (Standard)</label>
              </div>
              <FormGroup label="Beschluss-Art (Mehrheits-Berechnung)">
                <select className="form-control"
                  value={form.wahl?.abstimmungs_typ_id || ""}
                  onChange={e => setForm(f => ({ ...f, wahl: { ...(f.wahl || {}), abstimmungs_typ_id: e.target.value ? parseInt(e.target.value) : null } }))}>
                  <option value="">— Standard (einfache Mehrheit) —</option>
                  {abstimmungsTypenList.map(t => (
                    <option key={t.id} value={t.id}>
                      {t.bezeichnung} ({t.anwesenheits_quote_prozent}% Anwes. / {t.mehrheits_quote_prozent}% Mehrheit)
                    </option>
                  ))}
                </select>
              </FormGroup>
              <div style={{ marginTop: 14 }}>
                <div style={{ fontWeight: 600, marginBottom: 6, fontSize: 13 }}>Kandidaten</div>
                {(form.kandidaten || []).length === 0 && (
                  <div style={{ fontSize: 12, color: "var(--ink-faint)", marginBottom: 6 }}>Noch keine Kandidaten — bitte hinzufügen.</div>
                )}
                {(form.kandidaten || []).map((k, idx) => (
                  <div key={idx} style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 6 }}>
                    <span style={{ minWidth: 22, color: "var(--ink-faint)", fontSize: 13 }}>{idx + 1}.</span>
                    <input className="form-control" placeholder="Name *" style={{ flex: 2 }}
                      value={k.person_name || ""}
                      onChange={e => setForm(f => {
                        const arr = [...(f.kandidaten || [])];
                        arr[idx] = { ...arr[idx], person_name: e.target.value };
                        return { ...f, kandidaten: arr };
                      })} />
                    <input className="form-control" placeholder="E-Mail (optional)" style={{ flex: 2 }}
                      value={k.person_email || ""}
                      onChange={e => setForm(f => {
                        const arr = [...(f.kandidaten || [])];
                        arr[idx] = { ...arr[idx], person_email: e.target.value };
                        return { ...f, kandidaten: arr };
                      })} />
                    <button type="button" className="btn btn-ghost"
                      style={{ fontSize: 11, padding: "4px 8px" }}
                      onClick={() => setForm(f => ({
                        ...f, kandidaten: (f.kandidaten || []).filter((_, i) => i !== idx)
                      }))}>✕</button>
                  </div>
                ))}
                <button type="button" className="btn btn-secondary"
                  style={{ fontSize: 12, padding: "4px 10px", marginTop: 4 }}
                  onClick={() => setForm(f => ({
                    ...f, kandidaten: [...(f.kandidaten || []), { person_name: "", person_email: "" }]
                  }))}>+ Kandidat hinzufügen</button>
              </div>
            </div>
          )}
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
            <input type="checkbox" id="oeffentlich" checked={form.oeffentlich !== false}
              onChange={e => setForm(f => ({ ...f, oeffentlich: e.target.checked }))} />
            <label htmlFor="oeffentlich" style={{ fontSize: 13, cursor: "pointer" }}>Oeffentlicher Tagesordnungspunkt</label>
          </div>
          <FormGroup label="Titel *">
            <input className="form-control" value={form.titel} placeholder="z. B. Jahresbericht"
              onChange={e => setForm(f => ({ ...f, titel: e.target.value }))} />
          </FormGroup>
          <FormGroup label="Verknüpfte Vorlage (optional)">
            <select className="form-control" value={form.vorlage_id || ""}
              onChange={e => setForm(f => ({ ...f, vorlage_id: e.target.value ? parseInt(e.target.value, 10) : null }))}>
              <option value="">Keine</option>
              {vorlagenListe.map(v => (
                <option key={v.id} value={v.id}>{v.vorlagennummer ? `${v.vorlagennummer} – ` : ""}{v.titel}</option>
              ))}
            </select>
          </FormGroup>
          <FormGroup label="Beschreibung">
            <textarea className="form-control" value={form.beschreibung} rows={3}
              onChange={e => setForm(f => ({ ...f, beschreibung: e.target.value }))} />
          </FormGroup>
        </Modal>
      )}
    </>
  );
}
// ─── LADUNG TAB ──────────────────────────────────────────────────────────────
function LadungTab({ sitzung, reload, setError, setSuccess }) {
  const _role = useGremiumRole(sitzung?.gremium_id);
  const _isAdmin = useIsAdmin();
  const canVorsitz = _isAdmin || roleSatisfies(_role, "vorsitz");
  const canSchriftfuehrer = _isAdmin || roleSatisfies(_role, "schriftfuehrer");
  const { info: meInfo } = useMeInfo();
  const myEmail = meInfo?.email || null;

  const [emailText, setEmailText] = useState("");
  const [sending, setSending] = useState(false);
  const [empfaenger, setEmpfaenger] = useState(null);
  const [loadingEmpf, setLoadingEmpf] = useState(false);

  const loadEmpfaenger = async () => {
    setLoadingEmpf(true);
    try {
      const data = await api.getLadungEmpfaenger(sitzung.id);
      setEmpfaenger(data);
    } catch (e) {
      setError("Keine Mitglieder gefunden. Im internen Modus: Benutzer dem Gremium zuweisen. Im Keycloak-Modus: Keycloak-Gruppe konfigurieren.");
    } finally { setLoadingEmpf(false); }
  };

  useEffect(() => { loadEmpfaenger(); }, []);

  const sendMail = async () => {
    if (!sitzung.tops.length) { setError("Bitte zuerst die Tagesordnung anlegen."); return; }
    setSending(true);
    try {
      const res = await api.sendLadung(sitzung.id, { email_text: emailText || null });
      let msg = `Ladung versandt an ${res.versandt} Personen.`;
      if (res.fehler?.length) msg += ` ${res.fehler.length} Fehler: ${res.fehler.join(", ")}`;
      setSuccess(msg);
      reload();
    } catch { setError("Fehler beim Versand."); }
    finally { setSending(false); }
  };
  return (
    <div className="grid-2">
      <div>
        {/* Empfänger-Vorschau */}
        {canVorsitz && (<div className="card mb-16">
          <div className="card-header">
            <h3>Empfänger</h3>
            <button className="btn btn-ghost btn-sm ml-auto" onClick={loadEmpfaenger}>↻ Aktualisieren</button>
          </div>
          <div className="card-body" style={{ padding: 0 }}>
            {false ? (
              <div style={{ padding: "16px 20px" }}>
                <Alert type="error">
                  Gremium <strong>{sitzung.gremium?.name}</strong> hat keine Keycloak-Gruppe zugeordnet.
                  Bitte unter <strong>Gremien</strong> die Keycloak-Gruppe setzen.
                </Alert>
              </div>
            ) : loadingEmpf ? (
              <div style={{ padding: 20, color: "var(--ink-faint)", fontSize: 13 }}>Lade Mitglieder aus Keycloak…</div>
            ) : empfaenger ? (
              <>
                <div style={{ padding: "10px 20px", borderBottom: "1px solid var(--paper)",
                              fontSize: 12, color: "var(--ink-soft)", display: "flex", gap: 12 }}>
                  <span>Gruppe: <strong>{empfaenger.gruppe}</strong></span>
                  <span>Mitglieder: <strong>{empfaenger.anzahl}</strong></span>
                </div>
                <div style={{ maxHeight: 200, overflowY: "auto" }}>
                  {empfaenger.mitglieder.map((m, i) => (
                    <div key={i} style={{ display: "flex", gap: 12, padding: "7px 20px",
                                          borderBottom: "1px solid var(--paper)", fontSize: 13 }}>
                      <span style={{ fontWeight: 500, minWidth: 160 }}>{m.name}</span>
                      <span style={{ color: "var(--ink-faint)", fontFamily: "var(--mono)", fontSize: 12 }}>{m.email}</span>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div style={{ padding: "16px 20px" }}>
                <button className="btn btn-secondary btn-sm" onClick={loadEmpfaenger}>
                  Mitglieder aus Keycloak laden
                </button>
              </div>
            )}
          </div>
        </div>)}

        {/* Aktionen */}
        <div className="card mb-16">
          <div className="card-header"><h3>PDF</h3></div>
          <div className="card-body" style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <button className="btn btn-secondary" onClick={async () => {
              const token = localStorage.getItem("internal_token") || (await import("../auth/keycloak")).getKeycloak()?.token;
              const url_to_use = canVorsitz ? api.ladungPdfUrl(sitzung.id) : api.ladungPersonalPdfUrl(sitzung.id);
              const res = await fetch(url_to_use, { headers: { Authorization: "Bearer " + token } });
              const blob = await res.blob();
              const url = URL.createObjectURL(blob);
              window.open(url, "_blank");
            }}>⬇ {canVorsitz ? "Allgemeine Ladung (PDF)" : "Meine Ladung (PDF)"}</button>
            {canVorsitz && (<button className="btn btn-primary" onClick={async () => {
              const token = localStorage.getItem("internal_token") || (await import("../auth/keycloak")).getKeycloak()?.token;
              const res = await fetch(api.seriendruck_url(sitzung.id), { headers: { Authorization: "Bearer " + token } });
              const blob = await res.blob(); const a = document.createElement("a");
              a.href = URL.createObjectURL(blob); a.download = `${dayjs(sitzung.datum).format("YYYY_MM_DD")}_Seriendruck_${sitzung.aktenzeichen || sitzung.id}.zip`; a.click();
            }}>📄 Seriendruck (ZIP)</button>)}
          </div>
        </div>

        {canVorsitz && (<div className="card">
          <div className="card-header"><h3>Serienmail versenden</h3></div>
          <div className="card-body">
            <div style={{ fontSize: 12, color: "var(--ink-soft)", marginBottom: 12 }}>
              Jede Person erhält eine individuelle E-Mail mit persönlicher Anrede und eigenem PDF-Anhang.
            </div>
            <FormGroup label="Anschreiben (optional — leer = Standardtext)">
              <textarea className="form-control" rows={5} value={emailText}
                placeholder={`Guten Tag [Name],\n\nhiermit laden wir Sie ein…`}
                onChange={e => setEmailText(e.target.value)} />
            </FormGroup>
            {sitzung.ladung_versendet_am && (
              <Alert type="success">
                Zuletzt versandt: {dayjs(sitzung.ladung_versendet_am).format("DD.MM.YYYY HH:mm")} Uhr
              </Alert>
            )}
            {canVorsitz && (<button className="btn btn-primary" onClick={sendMail}
              disabled={sending || !sitzung.gremium?.keycloak_group_id}>
              {sending ? "Wird gesendet…" : `📧 Serienmail senden (${empfaenger?.anzahl ?? "?"} Personen)`}
            </button>)}
          </div>
        </div>)}
      </div>

      {/* Tagesordnungsvorschau */}
      <div className="card">
        <div className="card-header"><h3>Vorschau Tagesordnung</h3></div>
        <div className="card-body">
          {sitzung.tops.length === 0 ? (
            <div className="empty-state"><div className="empty-icon">📋</div><p>Noch keine TOPs</p></div>
          ) : (
            <ul className="top-list">
              {sitzung.tops.map(t => (
                <li key={t.id} className="top-item">
                  <div className="top-num">{t.position}</div>
                  <div className="top-content">
                    <div className="top-title">{t.titel}</div>
                    {t.beschreibung && <div className="top-desc">{t.beschreibung}</div>}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── PROTOKOLL TAB ───────────────────────────────────────────────────────────
function ProtokollTab({ sitzung, reload, setError, setSuccess, genehmigungen, loadGenehmigungen, setShowAblehnungDetail }) {
  const _role = useGremiumRole(sitzung?.gremium_id);
  const _isAdmin = useIsAdmin();
  const canVorsitz = _isAdmin || roleSatisfies(_role, "vorsitz");
  const canSchriftfuehrer = _isAdmin || roleSatisfies(_role, "schriftfuehrer");
  const { info: meInfo } = useMeInfo();
  const myEmail = meInfo?.email || null;


  // ───── Mitglied-Branch: kein Editor, nur Genehmigungs-Workflow ─────
  if (!canSchriftfuehrer) {
    const my = (genehmigungen || []).find(g => (g.person_email || "").toLowerCase() === (myEmail || "").toLowerCase());

    // Fall A: keine Genehmigungs-Anfrage für mich vorhanden
    if (!my) {
      return (
        <div className="card">
          <div className="card-body">
            <p style={{ margin: 0, color: "var(--ink-soft)" }}>
              Das Protokoll ist noch nicht zur Genehmigung freigegeben.
            </p>
          </div>
        </div>
      );
    }

    // Fall C: schon abgestimmt
    if (my.genehmigt) {
      return (
        <div>
          <Alert type="success">
            ✓ Sie haben das Protokoll am {my.genehmigt_am ? new Date(my.genehmigt_am).toLocaleString("de-DE") : "—"} genehmigt.
          </Alert>
          <ProtokollVorschau sitzung={sitzung} />
        </div>
      );
    }
    if (my.abgelehnt) {
      return (
        <div>
          <Alert type="warning">
            ✗ Sie haben das Protokoll am {my.abgelehnt_am ? new Date(my.abgelehnt_am).toLocaleString("de-DE") : "—"} abgelehnt.
            {my.ablehnungsgrund && (<><br />Begründung: {my.ablehnungsgrund}</>)}
          </Alert>
          <ProtokollVorschau sitzung={sitzung} />
        </div>
      );
    }

    // Fall B: offen – Genehmigen/Ablehnen
    const handleGenehmigen = async () => {
      if (!my.token) { alert("Token fehlt, bitte den Email-Link nutzen."); return; }
      if (!window.confirm("Möchten Sie das Protokoll genehmigen?")) return;
      try {
        const r = await fetch(`/protokoll-genehmigen?token=${encodeURIComponent(my.token)}`, { method: "GET" });
        if (r.ok) { setSuccess("Genehmigung übermittelt."); loadGenehmigungen(); }
        else { setError("Fehler beim Genehmigen"); }
      } catch(e) { setError("Fehler: " + e.message); }
    };
    const handleAblehnen = async () => {
      if (!my.token) { alert("Token fehlt, bitte den Email-Link nutzen."); return; }
      const grund = window.prompt("Begründung (optional):") || "";
      try {
        const fd = new FormData();
        fd.append("token", my.token);
        fd.append("grund", grund);
        const r = await fetch(`/protokoll-ablehnen-bestaetigen`, { method: "POST", body: fd });
        if (r.ok) { setSuccess("Ablehnung übermittelt."); loadGenehmigungen(); }
        else { setError("Fehler beim Ablehnen"); }
      } catch(e) { setError("Fehler: " + e.message); }
    };

    return (
      <div>
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="card-body">
            <h3 style={{ marginTop: 0 }}>Protokoll zur Genehmigung</h3>
            <p style={{ color: "var(--ink-soft)" }}>
              Das Protokoll wurde zur Genehmigung freigegeben. Bitte prüfen Sie den Inhalt und geben Sie Ihre Stimme ab.
            </p>
            <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
              <button className="btn btn-primary" onClick={handleGenehmigen}>✓ Genehmigen</button>
              <button className="btn btn-danger" onClick={handleAblehnen}>✗ Ablehnen</button>
            </div>
          </div>
        </div>
        <ProtokollVorschau sitzung={sitzung} />
      </div>
    );
  }
  // ───── Ende Mitglied-Branch ─────

  const [dringAntraege, setDringAntraege] = useState([]);
  const [showVerschiebenTop, setShowVerschiebenTop] = useState(null);
  const [naechsteSitzungenTop, setNaechsteSitzungenTop] = useState([]);
  const [zielSitzungIdTop, setZielSitzungIdTop] = useState("");
  useEffect(() => {
    api.getAntraege(sitzung.id).then(all => {
      setDringAntraege(all.filter(a => a.ist_dringlichkeit && a.status === 'angenommen'));
    }).catch(() => {});
  }, [sitzung.id]);
  const [entries, setEntries] = useState({});
  const [savedEntries, setSavedEntries] = useState({});
  const [mitglieder, setMitglieder] = useState([]);
  const [gaeste, setGaeste] = useState([]);
  const [anwesenheit, setAnwesenheit] = useState({});
  const [showAnwesenheit, setShowAnwesenheit] = useState(true);

  useEffect(() => {
    api.getMitgliederListe(sitzung.id).then(setMitglieder).catch(() => {});
    api.getGaeste(sitzung.id).then(setGaeste).catch(() => {});
    api.getAnwesenheit(sitzung.id).then(data => {
      const map = {};
      data.forEach(a => { map[a.person_sub || ("gast_" + a.gast_id)] = a.anwesend; });
      setAnwesenheit(map);
    }).catch(() => {});
    api.getProtokoll(sitzung.id).then(data => {
      const map = {}, texts = {};
      data.forEach(p => { map[p.top_id || "allg"] = p; texts[p.top_id || "allg"] = p.inhalt; });
      setSavedEntries(map); setEntries(texts);
    });
  }, [sitzung.id]);

  const toggleAnwesenheit = async (person) => {
    const key = person.sub || ("gast_" + person.gast_id);
    const neu = !anwesenheit[key];
    setAnwesenheit(prev => ({ ...prev, [key]: neu }));
    await api.setAnwesenheit(sitzung.id, {
      person_sub: person.sub || null,
      person_name: person.name,
      person_email: person.email || null,
      ist_gast: person.ist_gast || false,
      gast_id: person.gast_id || null,
      anwesend: neu,
    });
  };

  const save = async (topId) => {
    const key = topId || "allg";
    const text = entries[key] || "";
    try {
      if (savedEntries[key]) {
        await api.updateProtokoll(sitzung.id, savedEntries[key].id, { inhalt: text });
      } else {
        const p = await api.addProtokoll(sitzung.id, { inhalt: text, top_id: topId || null });
        setSavedEntries(prev => ({ ...prev, [key]: p }));
      }
    } catch { setError("Fehler beim Speichern"); }
  };

  // Baum -> flache Liste mit hierarchischer Nummer (z.B. "4.1"), analog zur
  // Tagesordnung - Unter-TOPs tauchten vorher NICHT im Protokoll auf, weil
  // sitzung.tops nur die Wurzel-TOPs enthaelt (Unterpunkte kommen
  // verschachtelt ueber top.children, siehe list_tops() im Backend), aber
  // hier nie aufgeklappt wurde.
  const flatten = (nodes, prefix = "") => {
    let out = [];
    (nodes || []).slice()
      .sort((a, b) => (a.position || 0) - (b.position || 0))
      .forEach((node, idx) => {
        const nummer = prefix ? `${prefix}.${idx + 1}` : `${idx + 1}`;
        out.push({ ...node, _nummer: nummer, _tiefe: prefix ? prefix.split(".").length : 0 });
        if (node.children && node.children.length) {
          out = out.concat(flatten(node.children, nummer));
        }
      });
    return out;
  };
  const allTops = [...flatten(sitzung.tops), { id: "allg", position: null, titel: "Allgemeine Notizen" }];
  const anwesendCount = Object.values(anwesenheit).filter(Boolean).length;
  return (
    <div>
      {/* Anwesenheit */}
      <div className="card mb-16">
        <div className="card-header" style={{ cursor: "pointer" }} onClick={() => setShowAnwesenheit(!showAnwesenheit)}>
          <h3>Anwesenheit ({anwesendCount} anwesend)</h3>
          <span style={{ marginLeft: "auto", color: "var(--ink-faint)" }}>{showAnwesenheit ? "▲" : "▼"}</span>
        </div>
        {showAnwesenheit && (
          <div className="card-body" style={{ padding: "8px 0" }}>
            {mitglieder.length > 0 && (
              <div style={{ padding: "6px 18px 4px", fontSize: 11, fontWeight: 600, color: "var(--ink-faint)", textTransform: "uppercase", letterSpacing: 0.5 }}>
                Gremiummitglieder
              </div>
            )}
            {mitglieder.map(m => (
              <div key={m.sub} style={{ display: "flex", alignItems: "center", gap: 12, padding: "7px 18px", borderBottom: "1px solid var(--paper)" }}>
                <input type="checkbox" checked={!!anwesenheit[m.sub]}
                  style={{ width: 16, height: 16, accentColor: "var(--red)", cursor: "pointer" }}
                  onChange={() => toggleAnwesenheit({ sub: m.sub, name: m.name, email: m.email })} />
                <div>
                  <div style={{ fontWeight: 500, fontSize: 13 }}>{m.name}</div>
                  <div style={{ fontSize: 11, color: "var(--ink-faint)" }}>{m.email}</div>
                </div>
              </div>
            ))}
            {gaeste.length > 0 && (
              <div style={{ padding: "10px 18px 4px", fontSize: 11, fontWeight: 600, color: "var(--info)", textTransform: "uppercase", letterSpacing: 0.5 }}>
                Externe Gäste
              </div>
            )}
            {gaeste.map(g => (
              <div key={"gast_" + g.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "7px 18px", borderBottom: "1px solid var(--paper)" }}>
                <input type="checkbox" checked={!!anwesenheit["gast_" + g.id]}
                  style={{ width: 16, height: 16, accentColor: "var(--info)", cursor: "pointer" }}
                  onChange={() => toggleAnwesenheit({ gast_id: g.id, name: g.vorname + " " + g.nachname, email: g.email, ist_gast: true })} />
                <div>
                  <div style={{ fontWeight: 500, fontSize: 13 }}>{g.nachname}, {g.vorname}
                    {g.freitext && <span style={{ fontSize: 11, color: "var(--ink-faint)", marginLeft: 8 }}>({g.freitext})</span>}
                  </div>
                  {g.email && <div style={{ fontSize: 11, color: "var(--ink-faint)" }}>{g.email}</div>}
                </div>
              </div>
            ))}
            {mitglieder.length === 0 && gaeste.length === 0 && (
              <div style={{ padding: "16px 18px", color: "var(--ink-faint)", fontSize: 13 }}>Keine Mitglieder oder Gäste gefunden</div>
            )}
          </div>
        )}
      </div>

      {/* Protokoll 2-spaltig */}
      <div className="grid-2">
        <div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
            <h3 style={{ fontSize: 15, fontWeight: 600 }}>Protokoll erfassen</h3>
            {canSchriftfuehrer && (<button className="btn btn-secondary btn-sm" onClick={async () => {
              const token = localStorage.getItem("internal_token") || (await import("../auth/keycloak")).getKeycloak()?.token;
              const res = await fetch(api.protokollPdfUrl(sitzung.id), { headers: { Authorization: "Bearer " + token } });
              const blob = await res.blob();
              const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = `${dayjs(sitzung.datum).format("YYYY_MM_DD")}_Protokoll_${sitzung.aktenzeichen || sitzung.id}.pdf`; a.click();
}}>⬇ Als PDF</button>)}
            {canSchriftfuehrer && (<button className="btn btn-primary btn-sm" onClick={async () => {
              try {
                const res = await api.sendProtokoll(sitzung.id);
                setSuccess(`Protokoll gesendet an: ${res.empfaenger.join(", ")}`);
              } catch(e) { setError("Fehler beim Senden. Empfänger in Einstellungen prüfen."); }
            }}>📧 Per E-Mail senden</button>)}
            {canSchriftfuehrer && (<button className="btn btn-primary btn-sm" onClick={async () => {
              try {
                const res = await api.genehmigungAnfordern(sitzung.id);
                setSuccess(`Genehmigungsanfrage gesendet an ${res.gesendet ? res.gesendet.length : "?"} Mitglieder.`);
                loadGenehmigungen();
              } catch(e) { console.error("Genehmigung Fehler:", e); setSuccess("Genehmigungsanfrage gesendet!"); loadGenehmigungen(); }
            }}>✅ Genehmigung anfordern</button>)}
          </div>

          {showVerschiebenTop && (
            <Modal title="TOP in andere Sitzung verschieben" onClose={() => setShowVerschiebenTop(null)}
              footer={<>
                <button className="btn btn-secondary" onClick={() => setShowVerschiebenTop(null)}>Abbrechen</button>
                <button className="btn btn-primary" disabled={!zielSitzungIdTop} onClick={async () => {
                  const ziel = naechsteSitzungenTop.find(s => s.id === parseInt(zielSitzungIdTop));
                  // Als Antrag in Zielsitzung anlegen
                  await api.topVerschieben(sitzung.id, showVerschiebenTop.id, { ziel_sitzung_id: parseInt(zielSitzungIdTop) });
                  setShowVerschiebenTop(null);
                  reload();
                }}>Verschieben</button>
              </>}>
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>TOP:</div>
                <div style={{ fontSize: 13, color: "var(--ink-soft)" }}>{showVerschiebenTop.titel}</div>
              </div>
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, color: "var(--ink-soft)", marginBottom: 8 }}>Zielsitzung:</div>
                {naechsteSitzungenTop.length === 0 ? (
                  <div style={{ color: "var(--danger)", fontSize: 13 }}>Keine kommenden Sitzungen gefunden. Bitte zuerst anlegen.</div>
                ) : (
                  <select className="form-control" value={zielSitzungIdTop} onChange={e => setZielSitzungIdTop(e.target.value)}>
                    <option value="">— Bitte wählen —</option>
                    {naechsteSitzungenTop.map(s => (
                      <option key={s.id} value={s.id}>
                        {s.aktenzeichen} — {s.titel} ({dayjs(s.datum).format("DD.MM.YYYY")})
                      </option>
                    ))}
                  </select>
                )}
              </div>
            </Modal>
          )}
          {dringAntraege.length > 0 && (
            <div style={{ marginTop: 24, marginBottom: 16 }}>
              <h3 style={{ fontSize: 14, fontWeight: 600, color: "var(--warn)", marginBottom: 12 }}>
                ⚡ Dringlichkeitsanträge — Abstimmung Zulassung zur Tagesordnung
              </h3>
              {dringAntraege.map(a => (
                <DringlichkeitZulassung key={a.id} antrag={a} sitzungId={sitzung.id} reload={reload} />
              ))}
            </div>
          )}
          {genehmigungen.length > 0 && (
            <div className="card" style={{ marginTop: 16 }}>
              <div className="card-header">
                <h3>Genehmigungsstatus</h3>
                <span style={{ marginLeft: 8, fontSize: 12, color: "var(--ink-soft)" }}>
                  {genehmigungen.filter(g => g.genehmigt).length} / {genehmigungen.length} genehmigt
                </span>
                {genehmigungen.length > 0 && genehmigungen.every(g => g.genehmigt) && (
                  <span style={{ marginLeft: 8, color: "var(--ok)", fontWeight: 600, fontSize: 12 }}>✓ Alle genehmigt — Freigegeben!</span>
                )}
              </div>
              <table className="table">
                <thead><tr><th>Name</th><th>E-Mail</th><th>Status</th><th>Genehmigt am</th></tr></thead>
                <tbody>
                  {genehmigungen.map(g => (
                    <tr key={g.id}>
                      <td>{g.person_name}</td>
                      <td style={{ fontSize: 12 }}>{g.person_email}</td>
                      <td>
                        {g.genehmigt
                          ? <span style={{ color: "var(--ok)", fontWeight: 600 }}>✓ Genehmigt</span>
                          : g.abgelehnt
                            ? <button className="btn btn-ghost btn-sm" style={{ color: "var(--danger)", fontWeight: 600, padding: "2px 6px" }}
                                onClick={() => setShowAblehnungDetail(g)}>✕ Abgelehnt 🔍</button>
                            : <span style={{ color: "var(--warn)" }}>⏳ Ausstehend</span>}
                      </td>
                      <td style={{ fontSize: 12 }}>
                        {g.genehmigt && dayjs(g.genehmigt_am).format("DD.MM.YYYY HH:mm")}
                        {g.abgelehnt && dayjs(g.abgelehnt_am).format("DD.MM.YYYY HH:mm")}
                        {!g.genehmigt && !g.abgelehnt && "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {allTops.map(top => (
            <div key={top.id} className="card mb-16" style={{ marginLeft: (top._tiefe || 0) * 24 }}>
              <div className="card-header">
                {top.position !== null && (
                  <div style={{ width: 26, height: 26, borderRadius: "50%", background: "var(--red)", color: "white",
                    display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, flexShrink: 0 }}>
                    {top._nummer || top.position}
                  </div>
                )}
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <h3 style={{ fontSize: 13 }}>{top.titel}</h3>
                  {top.id !== "allg" && (
                    <button className="btn btn-ghost btn-sm" style={{ color: "var(--info)", fontSize: 11 }}
                      onClick={async () => {
                        try {
                          const res = await api.topVerschiebenSmart(sitzung.id, top.id, {});
                          const msg = res.neu_angelegt
                            ? `TOP verschoben. Neue Sitzung ${res.aktenzeichen} angelegt — bitte Datum und Ort nachtragen!`
                            : `TOP verschoben nach: ${res.ziel} (${res.aktenzeichen})`;
                          alert(msg);
                          reload();
                        } catch(e) { alert("Fehler beim Verschieben"); }
                      }}>→ Verschieben</button>
                  )}
                </div>
                {savedEntries[top.id] && <span style={{ fontSize: 11, color: "var(--ok)", marginLeft: "auto" }}>✓</span>}
              </div>
              <div className="card-body">
                <textarea className="form-control" rows={4}
                  placeholder={top.id === "allg" ? "Allgemeine Notizen…" : `Protokollnotiz zu TOP ${top._nummer || top.position}…`}
                  value={entries[top.id] || ""}
                  onChange={e => setEntries(prev => ({ ...prev, [top.id]: e.target.value }))} />
              {top.id !== "allg" && top.top_typ === "beschluss" && (
                <AbstimmungBlock top={top} sitzungId={sitzung.id} reload={reload} />
              )}
                <div style={{ marginTop: 8, display: "flex", justifyContent: "flex-end" }}>
                  <button className="btn btn-primary btn-sm" onClick={() => save(top.id === "allg" ? null : top.id)}>Speichern</button>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="card" style={{ height: "fit-content", position: "sticky", top: 20 }}>
          <div className="card-header"><h3>Vorschau</h3></div>
          <div style={{ padding: "8px 0" }}>
            {sitzung.tops.length === 0 ? (
              <div className="empty-state"><div className="empty-icon">📋</div><p>Noch keine TOPs</p></div>
            ) : sitzung.tops.map(top => (
              <div key={top.id} style={{ padding: "10px 18px", borderBottom: "1px solid var(--paper)" }}>
                <div style={{ display: "flex", gap: 8 }}>
                  <div style={{ width: 22, height: 22, borderRadius: "50%", background: "var(--red)", color: "white",
                    display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 700, flexShrink: 0 }}>
                    {top.position}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 500, fontSize: 13 }}>{top.titel}</div>
                    {entries[top.id] ? (
                      <div style={{ fontSize: 12, color: "var(--ink-soft)", marginTop: 4, background: "var(--paper)",
                        borderLeft: "3px solid var(--red)", padding: "6px 10px" }}>{entries[top.id]}</div>
                    ) : (
                      <div style={{ fontSize: 11, color: "var(--border-strong)", marginTop: 2 }}>— noch keine Notiz —</div>
                    )}
                    {top.top_typ === "beschluss" && top.abstimmung_ergebnis && (
                      <div style={{ marginTop: 6, background: "var(--paper)", border: "1px solid var(--border-strong)", padding: "6px 10px", fontSize: 11 }}>
                        {(() => { const t = top; return (
                          <div>
                            <span style={{ color: t.abstimmung_ergebnis === "angenommen" ? "var(--ok)" : "var(--danger)", fontWeight: 600 }}>
                              {t.abstimmung_ergebnis === "angenommen" ? "✓ Angenommen" : "✕ Abgelehnt"}
                            </span>
                            <span style={{ color: "var(--ink-soft)", marginLeft: 6 }}>
                              (J:{t.abstimmung_ja} N:{t.abstimmung_nein} E:{t.abstimmung_enthaltung})
                            </span>
                          </div>
                        ); })()}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
            {entries["allg"] && (
              <div style={{ padding: "10px 18px" }}>
                <div style={{ fontWeight: 500, fontSize: 13, marginBottom: 4 }}>Allgemeine Notizen</div>
                <div style={{ fontSize: 12, color: "var(--ink-soft)", background: "var(--paper)",
                  borderLeft: "3px solid var(--red)", padding: "6px 10px" }}>{entries["allg"]}</div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}


// ─── BESCHLÜSSE TAB ──────────────────────────────────────────────────────────
function BeschluesseTab({ sitzung, reload }) {
  const _role = useGremiumRole(sitzung?.gremium_id);
  const _isAdmin = useIsAdmin();
  const canVorsitz = _isAdmin || roleSatisfies(_role, "vorsitz");
  const canSchriftfuehrer = _isAdmin || roleSatisfies(_role, "schriftfuehrer");
  const { info: meInfo } = useMeInfo();
  const myEmail = meInfo?.email || null;

  const [showModal, setShowModal] = useState(false);
  const [gremiumMitglieder, setGremiumMitglieder] = useState([]);
  useEffect(() => {
    if (!showModal) return;
    api.getLadungEmpfaenger(sitzung.id)
      .then(d => setGremiumMitglieder(d?.mitglieder || []))
      .catch(() => setGremiumMitglieder([]));
  }, [showModal, sitzung.id]);
  const [form, setForm] = useState({ top_id: "", text: "", verantwortlich: "", beauftragter_email: "", faellig_am: "", status: "offen" });
  const allBeschluesse = sitzung.tops.flatMap(t => t.beschluesse || []);

  const oeffnePdf = async (url) => {
    try {
      const token = localStorage.getItem("internal_token") || (await import("../auth/keycloak")).getKeycloak()?.token;
      const res = await fetch(url, { headers: { Authorization: "Bearer " + token } });
      if (!res.ok) { alert("PDF konnte nicht geladen werden (" + res.status + ")"); return; }
      const blob = await res.blob();
      window.open(URL.createObjectURL(blob), "_blank");
    } catch (e) { alert("Fehler: " + e.message); }
  };

  const downloadZip = async () => {
    try {
      const token = localStorage.getItem("internal_token") || (await import("../auth/keycloak")).getKeycloak()?.token;
      const res = await fetch(`/api/sitzungen/${sitzung.id}/beschluesse.zip`, { headers: { Authorization: "Bearer " + token } });
      if (!res.ok) { alert("ZIP konnte nicht erstellt werden (" + res.status + ")"); return; }
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `Beschluesse_${dayjs(sitzung.datum).format("YYYY_MM_DD")}_${sitzung.aktenzeichen || sitzung.id}.zip`;
      a.click();
    } catch (e) { alert("Fehler: " + e.message); }
  };


  const save = async () => {
    await api.createBeschluss({ ...form, top_id: Number(form.top_id), faellig_am: form.faellig_am || null });
    setShowModal(false); reload();
  };

  const updateStatus = async (b, status) => {
    await api.updateBeschluss(b.id, { ...b, status });
    reload();
  };
  return (
    <>
      <div className="card">
        <div className="card-header">
          <h3>Beschlüsse ({allBeschluesse.length})</h3>
          <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
            {allBeschluesse.length > 0 && (
              <>
                <button className="btn btn-ghost btn-sm" onClick={() => oeffnePdf(`/api/sitzungen/${sitzung.id}/beschluesse.pdf`)}>
                  📄 Sammel-PDF
                </button>
                <button className="btn btn-ghost btn-sm" onClick={downloadZip}>
                  📦 ZIP
                </button>
              </>
            )}
            {canVorsitz && (<button className="btn btn-primary btn-sm" onClick={() => {
              setForm({ top_id: sitzung.tops[0]?.id || "", text: "", verantwortlich: "", faellig_am: "", status: "offen" });
              setShowModal(true);
            }}>+ Beschluss erfassen</button>)}
          </div>
        </div>
        <div className="card-body" style={{ padding: 0 }}>
          {allBeschluesse.length === 0 ? (
            <div className="empty-state"><div className="empty-icon">✓</div><p>Noch keine Beschlüsse</p></div>
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th>TOP</th><th>Beschluss</th><th>Verantwortlich</th><th>Fällig</th><th>Status</th><th></th>
                </tr>
              </thead>
              <tbody>
                {sitzung.tops.flatMap(top =>
                  (top.beschluesse || []).map(b => (
                    <tr key={b.id}>
                      <td style={{ fontSize: 12, color: "var(--ink-soft)" }}>{top.position}. {top.titel}</td>
                      <td style={{ maxWidth: 300 }}>{b.text}</td>
                      <td>{b.verantwortlich || "—"}</td>
                      <td style={{ fontFamily: "var(--mono)", fontSize: 12 }}>
                        {b.faellig_am ? dayjs(b.faellig_am).format("DD.MM.YYYY") : "—"}
                      </td>
                      <td><Badge status={b.status} /></td>
                      <td style={{ display: "flex", gap: 6, alignItems: "center" }}>
                        <select className="form-control" style={{ width: "auto", padding: "3px 6px", fontSize: 12 }}
                          value={b.status} onChange={e => updateStatus(b, e.target.value)}>
                          <option value="offen">Offen</option>
                          <option value="in_bearbeitung">In Bearbeitung</option>
                          <option value="erledigt">Erledigt</option>
                          <option value="abgelehnt">Abgelehnt</option>
                        </select>
                        <button className="btn btn-ghost btn-sm" title="Beschluss als PDF"
                          onClick={() => oeffnePdf(`/api/sitzungen/${sitzung.id}/beschluesse/${b.id}/beschluss.pdf`)}>
                          📄
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {showModal && (
        <Modal title="Beschluss erfassen" onClose={() => setShowModal(false)}
          footer={<>
            <button className="btn btn-secondary" onClick={() => setShowModal(false)}>Abbrechen</button>
            <button className="btn btn-primary" onClick={save}>Speichern</button>
          </>}>
          <FormGroup label="Tagesordnungspunkt">
            <select className="form-control" value={form.top_id}
              onChange={e => setForm(f => ({ ...f, top_id: e.target.value }))}>
              {sitzung.tops.map(t => <option key={t.id} value={t.id}>{t.position}. {t.titel}</option>)}
            </select>
          </FormGroup>
          <FormGroup label="Beschlusstext *">
            <textarea className="form-control" rows={4} value={form.text}
              onChange={e => setForm(f => ({ ...f, text: e.target.value }))} />
          </FormGroup>
          <div className="form-row">
            <FormGroup label="Verantwortlich">
              <input className="form-control" value={form.verantwortlich}
                onChange={e => setForm(f => ({ ...f, verantwortlich: e.target.value }))} />

            <label style={{ display: "block", fontSize: 12, color: "var(--ink-soft)", marginTop: 12, marginBottom: 4 }}>Beauftragter (aus Gremium — darf Status ändern)</label>
            <select className="form-control"
              value={form.beauftragter_email || ""}
              onChange={e => setForm({ ...form, beauftragter_email: e.target.value })}>
              <option value="">— bitte wählen —</option>
              {gremiumMitglieder.map(m => (
                <option key={m.email} value={m.email}>{m.name} ({m.email})</option>
              ))}
            </select>
            <div style={{ fontSize: 11, color: "var(--ink-faint)", marginTop: 2 }}>
              Wenn keine Auswahl: Nur Vorsitz/Admin können Status ändern.
            </div>
            </FormGroup>
            <FormGroup label="Fällig am">
              <input type="date" className="form-control" value={form.faellig_am}
                onChange={e => setForm(f => ({ ...f, faellig_am: e.target.value }))} />
            </FormGroup>
          </div>
        </Modal>
      )}
    </>
  );
}

function AntraegeTab({ sitzung, reload, setError, setSuccess }) {
  const _role = useGremiumRole(sitzung?.gremium_id);
  const _isAdmin = useIsAdmin();
  const canVorsitz = _isAdmin || roleSatisfies(_role, "vorsitz");
  const canSchriftfuehrer = _isAdmin || roleSatisfies(_role, "schriftfuehrer");
  const { info: meInfo } = useMeInfo();
  const myEmail = meInfo?.email || null;

  const [umwandelnAntrag, setUmwandelnAntrag] = useState(null);
  const [umwandelnText, setUmwandelnText] = useState("");
  const [umwandelnVerantwortlich, setUmwandelnVerantwortlich] = useState("");
  const [umwandelnFaellig, setUmwandelnFaellig] = useState("");
  const [beschlussInfo, setBeschlussInfo] = useState({});

  useEffect(() => {
    const items = sitzung.antraege || [];
    items.forEach(a => {
      api.antragBeschlussInfo(sitzung.id, a.id).then(info => {
        setBeschlussInfo(prev => ({ ...prev, [a.id]: info }));
      }).catch(() => {});
    });
  }, [sitzung.id, sitzung.antraege]);

  const oeffneUmwandeln = (a) => {
    setUmwandelnAntrag(a);
    setUmwandelnText(a.beschlussvorlage || "");
    setUmwandelnVerantwortlich("");
    setUmwandelnFaellig("");
  };

  const fuehreUmwandlungAus = async () => {
    if (!umwandelnText.trim()) { setError("Beschluss-Wortlaut fehlt."); return; }
    try {
      const res = await api.inBeschlussUmwandeln(sitzung.id, umwandelnAntrag.id, {
        text: umwandelnText.trim(),
        verantwortlich: umwandelnVerantwortlich.trim() || null,
        faellig_am: umwandelnFaellig || null,
      });
      setBeschlussInfo(prev => ({ ...prev, [umwandelnAntrag.id]: { hat_beschluss: true, beschluss_id: res.beschluss_id } }));
      setSuccess(`Beschluss Nr. ${res.beschluss_id} angelegt.`);
      setUmwandelnAntrag(null);
      reload();
    } catch (e) {
      setError("Fehler: " + (e.message || "Umwandlung fehlgeschlagen"));
    }
  };

  const oeffnePdf = async (url) => {
    try {
      const token = localStorage.getItem("internal_token") || (await import("../auth/keycloak")).getKeycloak()?.token;
      const res = await fetch(url, { headers: { Authorization: "Bearer " + token } });
      if (!res.ok) { setError("PDF konnte nicht geladen werden (" + res.status + ")"); return; }
      const blob = await res.blob();
      window.open(URL.createObjectURL(blob), "_blank");
    } catch (e) { setError("Fehler: " + e.message); }
  };

  const { user } = useAuth();
  const [antraege, setAntraege] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editAntrag, setEditAntrag] = useState(null);
  const [showVerschieben, setShowVerschieben] = useState(null);
  const [naechsteSitzungen, setNaechsteSitzungen] = useState([]);
  const [zielSitzungId, setZielSitzungId] = useState("");
  const [form, setForm] = useState({ titel: "", beschreibung: "", beschlussvorlage: "", ist_dringlichkeit: false });

  const loadAntraege = () => api.getAntraege(sitzung.id).then(setAntraege).catch(() => {});
  useEffect(() => { loadAntraege(); }, [sitzung.id]);

  const submit = async () => {
    if (!form.titel) { setError("Bitte Titel angeben."); return; }
    try {
      if (editAntrag) {
        await api.updateAntrag(sitzung.id, editAntrag.id, form);
        setSuccess("Antrag aktualisiert!");
      } else {
        await api.createAntrag(sitzung.id, form);
        setSuccess("Antrag eingereicht!");
      }
      setShowForm(false);
      setEditAntrag(null);
      setForm({ titel: "", beschreibung: "", beschlussvorlage: "", ist_dringlichkeit: false });
      loadAntraege();
    } catch { setError("Fehler beim Speichern."); }
  };

  const openEdit = (a) => {
    setEditAntrag(a);
    setForm({ titel: a.titel, beschreibung: a.beschreibung || "", beschlussvorlage: a.beschlussvorlage || "" });
    setShowForm(true);
  };

  const entscheidung = async (aid, status, grund) => {
    try {
      await api.entscheidungAntrag(sitzung.id, aid, { status, ablehnungsgrund: grund });
      setSuccess(status === "angenommen" ? "Antrag angenommen — TOP wurde angelegt." : "Antrag abgelehnt.");
      loadAntraege(); reload();
    } catch { setError("Fehler bei der Entscheidung."); }
  };

  const statusColor = {
    eingereicht: { bg: "var(--warn-bg)", color: "var(--warn)" },
    angenommen: { bg: "var(--ok-bg)", color: "var(--ok)" },
    abgelehnt: { bg: "var(--paper)", color: "var(--ink-faint)" }
  };
  const statusLabel = { eingereicht: "Eingereicht", angenommen: "Angenommen", abgelehnt: "Abgelehnt" };
  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <h3 style={{ fontSize: 15, fontWeight: 600 }}>TOP-Anträge ({antraege.length})</h3>
        <button className="btn btn-primary btn-sm" onClick={() => setShowForm(true)}>+ TOP einreichen</button>
      </div>

      {antraege.length === 0 ? (
        <div className="empty-state"><div className="empty-icon">📝</div><p>Noch keine Anträge eingereicht</p></div>
      ) : antraege.map(a => (
        <div key={a.id} className="card mb-16">
          <div className="card-header">
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600 }}>{a.titel}</div>
              <div style={{ fontSize: 12, color: "var(--ink-faint)" }}>von {a.einreicher_name} · {dayjs(a.created_at).format("DD.MM.YYYY HH:mm")}</div>
            </div>
            <span style={{ padding: "3px 10px", borderRadius: 99, fontSize: 11, fontWeight: 600,
                           background: statusColor[a.status]?.bg, color: statusColor[a.status]?.color }}>
              {statusLabel[a.status]}
            </span>
          </div>
          <div className="card-body">
            {a.beschreibung && <p style={{ marginBottom: 8, fontSize: 13 }}>{a.beschreibung}</p>}
            {a.beschlussvorlage && (
              <div style={{ background: "var(--danger-bg)", border: "1px solid #e8c9c7", borderRadius: 4, padding: "8px 12px", fontSize: 13 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: "var(--red)", marginBottom: 4 }}>BESCHLUSSVORLAGE</div>
                {a.beschlussvorlage}
              </div>
            )}
            {a.ablehnungsgrund && (
              <div style={{ background: "var(--paper)", borderRadius: 4, padding: "8px 12px", fontSize: 13, marginTop: 8 }}>
                <strong>Ablehnungsgrund:</strong> {a.ablehnungsgrund}
              </div>
            )}
            {a.status === "eingereicht" && (
              <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
                {a.einreicher_sub === user?.sub && (
                  <button className="btn btn-secondary btn-sm" onClick={() => openEdit(a)}>
                    ✎ Bearbeiten
                  </button>
                )}
                <button className="btn btn-primary btn-sm" onClick={() => entscheidung(a.id, "angenommen", null)}>
                  ✓ Annehmen & TOP anlegen
                </button>
                <button className="btn btn-danger btn-sm" onClick={() => {
                  const grund = prompt("Ablehnungsgrund (optional):");
                  entscheidung(a.id, "abgelehnt", grund);
                }}>✕ Ablehnen</button>
              </div>
            )}
            <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
              {(canVorsitz || a.einreicher_email === myEmail) && (a.status === "eingereicht" || a.status === "angenommen" || a.status === "abgesetzt") && (
                <button className="btn btn-ghost btn-sm" style={{ color: "var(--ink-faint)" }} onClick={() => {
                  const grund = window.prompt("Begründung für Rücknahme (optional):");
                  if (grund !== null) {
                    api.antragZurueckziehen(sitzung.id, a.id, { grund }).then(() => { loadAntraege(); reload(); });
                  }
                }}>↩ Zurückziehen</button>
              )}
              {canVorsitz && (a.status === "eingereicht" || a.status === "angenommen") && (
                <button className="btn btn-ghost btn-sm" style={{ color: "var(--warn)" }} onClick={() => {
                  const grund = window.prompt("Begründung für Absetzung von TO (optional):");
                  if (grund !== null) {
                    api.antragAbsetzen(sitzung.id, a.id, { begruendung: grund }).then(() => { loadAntraege(); reload(); });
                  }
                }}>⊘ Von TO absetzen</button>
              )}
              {canVorsitz && (a.status === "eingereicht" || a.status === "angenommen" || a.status === "abgesetzt") && (
                <button className="btn btn-ghost btn-sm" style={{ color: "var(--info)" }} onClick={async () => {
                  try {
                    const sitzungen = await api.getNaechsteSitzungen(sitzung.id);
                    if (sitzungen.length > 1) {
                      setNaechsteSitzungen(sitzungen);
                      setShowVerschieben(a);
                      setZielSitzungId("");
                    } else {
                      const res = await api.antragVerschieben(sitzung.id, a.id,
                        sitzungen.length === 1 ? { ziel_sitzung_id: sitzungen[0].id } : {});
                      const msg = res.neu_angelegt
                        ? "Antrag verschoben. Neue Sitzung angelegt — bitte Datum nachtragen!"
                        : "Antrag verschoben nach: " + res.ziel;
                      alert(msg);
                      loadAntraege(); reload();
                    }
                  } catch(e) { alert("Fehler beim Verschieben"); }
                }}>→ Verschieben</button>
              )}
            </div>
            <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
              <button type="button"
                 className="btn btn-ghost btn-sm"
                 onClick={() => oeffnePdf(api.beschlussvorlagePdfUrl(sitzung.id, a.id))}>
                📄 Beschlussvorlage
              </button>
              {a.top_id && !beschlussInfo[a.id]?.hat_beschluss && (
                <button className="btn btn-secondary btn-sm" onClick={() => oeffneUmwandeln(a)}>
                  ⚖️ In Beschluss umwandeln
                </button>
              )}
              {beschlussInfo[a.id]?.hat_beschluss && (
                <button type="button"
                   className="btn btn-primary btn-sm"
                   onClick={() => oeffnePdf(api.beschlussPdfUrl(sitzung.id, beschlussInfo[a.id].beschluss_id))}>
                  ⚖️ Beschluss (Nr. {beschlussInfo[a.id].beschluss_id})
                </button>
              )}
            </div>

            {a.status === "angenommen" && <div style={{ marginTop: 8, fontSize: 12, color: "var(--ok)" }}>✓ Als TOP in der Tagesordnung</div>}
          </div>
        </div>
      ))}

      {showForm && (
        <Modal title={editAntrag ? "Antrag bearbeiten" : "TOP einreichen"} onClose={() => { setShowForm(false); setEditAntrag(null); setForm({ titel: "", beschreibung: "", beschlussvorlage: "", ist_dringlichkeit: false }); }}
          footer={<>
            <button className="btn btn-secondary" onClick={() => setShowForm(false)}>Abbrechen</button>
            <button className="btn btn-primary" onClick={submit}>Einreichen</button>
          </>}>
          <FormGroup label="Titel *">
            <input className="form-control" value={form.titel} placeholder="Titel des Tagesordnungspunkts"
              onChange={e => setForm(f => ({ ...f, titel: e.target.value }))} />
          </FormGroup>
          <FormGroup label="Beschreibung">
            <textarea className="form-control" rows={3} value={form.beschreibung}
              onChange={e => setForm(f => ({ ...f, beschreibung: e.target.value }))} />
          </FormGroup>
          {!editAntrag && dayjs().isAfter(dayjs(sitzung.antraege_frist)) && (
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16, background: "var(--warn-bg)", border: "1px solid var(--warn-bg)", borderRadius: 6, padding: "10px 14px" }}>
              <input type="checkbox" id="ist_dringlichkeit" checked={form.ist_dringlichkeit || false}
                onChange={e => setForm(f => ({ ...f, ist_dringlichkeit: e.target.checked }))} />
              <label htmlFor="ist_dringlichkeit" style={{ fontSize: 13, cursor: "pointer", fontWeight: 600, color: "var(--warn)" }}>
                ⚡ Dringlichkeitsantrag (Antragsfrist abgelaufen)
              </label>
            </div>
          )}
          <FormGroup label="Beschlussvorlage (optional)">
            <textarea className="form-control" rows={3} value={form.beschlussvorlage}
              placeholder="Der Ausschuss beschließt…"
              onChange={e => setForm(f => ({ ...f, beschlussvorlage: e.target.value }))} />
          </FormGroup>
        </Modal>
      )}
      {umwandelnAntrag && (
        <Modal title={`Antrag „${umwandelnAntrag.titel}" in Beschluss umwandeln`}
          onClose={() => setUmwandelnAntrag(null)}
          footer={<>
            <button className="btn btn-secondary" onClick={() => setUmwandelnAntrag(null)}>Abbrechen</button>
            <button className="btn btn-primary" onClick={fuehreUmwandlungAus}>⚖️ Beschluss anlegen</button>
          </>}>
          <FormGroup label="Beschluss-Wortlaut *">
            <textarea className="form-control" rows={8} value={umwandelnText}
              onChange={e => setUmwandelnText(e.target.value)}
              placeholder="Wortlaut des Beschlusses ..." />
            <div style={{ fontSize: 11, color: "var(--ink-faint)", marginTop: 4 }}>
              Vorgefüllt aus der Beschlussvorlage – ggf. anpassen.
            </div>
          </FormGroup>
          <FormGroup label="Verantwortlich (optional)">
            <input type="text" className="form-control" value={umwandelnVerantwortlich}
              onChange={e => setUmwandelnVerantwortlich(e.target.value)}
              placeholder="z. B. Wehrführer" />
          </FormGroup>
          <FormGroup label="Fällig bis (optional)">
            <input type="date" className="form-control" value={umwandelnFaellig}
              onChange={e => setUmwandelnFaellig(e.target.value)} />
          </FormGroup>
        </Modal>
      )}
    </div>

  );
}

// ─── GÄSTE TAB ───────────────────────────────────────────────────────────────
function GaesteTab({ sitzung, setError, setSuccess }) {
  const _role = useGremiumRole(sitzung?.gremium_id);
  const _isAdmin = useIsAdmin();
  const canVorsitz = _isAdmin || roleSatisfies(_role, "vorsitz");
  const canSchriftfuehrer = _isAdmin || roleSatisfies(_role, "schriftfuehrer");
  const { info: meInfo } = useMeInfo();
  const myEmail = meInfo?.email || null;

  const [gaeste, setGaeste] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editGast, setEditGast] = useState(null);
  const [form, setForm] = useState({ vorname: "", nachname: "", anschrift: "", email: "", freitext: "" });

  const load = () => api.getGaeste(sitzung.id).then(setGaeste).catch(() => {});
  useEffect(() => { load(); }, [sitzung.id]);

  const save = async () => {
    if (!form.vorname || !form.nachname) { setError("Bitte Vor- und Nachname angeben."); return; }
    try {
      if (editGast) await api.updateGast(sitzung.id, editGast.id, form);
      else await api.createGast(sitzung.id, form);
      setShowForm(false); setEditGast(null);
      setForm({ vorname: "", nachname: "", anschrift: "", email: "", freitext: "" });
      setSuccess(editGast ? "Gast aktualisiert." : "Gast hinzugefügt.");
      load();
    } catch { setError("Fehler beim Speichern."); }
  };

  const openEdit = (g) => {
    setEditGast(g);
    setForm({ vorname: g.vorname, nachname: g.nachname, anschrift: g.anschrift || "", email: g.email || "", freitext: g.freitext || "" });
    setShowForm(true);
  };

  const del = async (g) => {
    if (!window.confirm(`${g.vorname} ${g.nachname} entfernen?`)) return;
    await api.deleteGast(sitzung.id, g.id); load();
  };
  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <h3 style={{ fontSize: 15, fontWeight: 600 }}>Externe Gäste ({gaeste.length})</h3>
        <button className="btn btn-primary btn-sm" onClick={() => { setEditGast(null); setForm({ vorname: "", nachname: "", anschrift: "", email: "", freitext: "" }); setShowForm(true); }}>
          + Gast einladen
        </button>
      </div>

      {gaeste.length === 0 ? (
        <div className="empty-state"><div className="empty-icon">👤</div><p>Keine externen Gäste eingeladen</p></div>
      ) : (
        <div className="card">
          <table className="table">
            <thead>
              <tr><th>Name</th><th>E-Mail</th><th>Anschrift</th><th>Freitext</th><th></th></tr>
            </thead>
            <tbody>
              {gaeste.map(g => (
                <tr key={g.id}>
                  <td><strong>{g.nachname}, {g.vorname}</strong></td>
                  <td style={{ fontFamily: "var(--mono)", fontSize: 12 }}>{g.email || "—"}</td>
                  <td style={{ fontSize: 12, color: "var(--ink-soft)" }}>{g.anschrift || "—"}</td>
                  <td style={{ fontSize: 12, color: "var(--ink-soft)" }}>{g.freitext || "—"}</td>
                  <td>
                    <div style={{ display: "flex", gap: 4 }}>
                      <button className="btn btn-ghost btn-sm btn-icon" title="Bearbeiten" aria-label="Bearbeiten" onClick={() => openEdit(g)}>✎</button>
                      <button className="btn btn-ghost btn-sm btn-icon" title="Löschen" aria-label="Löschen" onClick={() => del(g)}>🗑</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showForm && (
        <Modal title={editGast ? "Gast bearbeiten" : "Externen Gast einladen"} onClose={() => setShowForm(false)}
          footer={<>
            <button className="btn btn-secondary" onClick={() => setShowForm(false)}>Abbrechen</button>
            <button className="btn btn-primary" onClick={save}>Speichern</button>
          </>}>
          <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
            <div style={{ flex: "1 1 45%", minWidth: 180 }}>
              <FormGroup label="Vorname *">
                <input className="form-control" value={form.vorname} onChange={e => setForm(f => ({ ...f, vorname: e.target.value }))} />
              </FormGroup>
            </div>
            <div style={{ flex: "1 1 45%", minWidth: 180 }}>
              <FormGroup label="Nachname *">
                <input className="form-control" value={form.nachname} onChange={e => setForm(f => ({ ...f, nachname: e.target.value }))} />
              </FormGroup>
            </div>
            <div style={{ flex: "1 1 45%", minWidth: 180 }}>
              <FormGroup label="E-Mail">
                <input type="email" className="form-control" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} />
              </FormGroup>
            </div>
            <div style={{ flex: "1 1 45%", minWidth: 180 }}>
              <FormGroup label="Anschrift">
                <input className="form-control" value={form.anschrift} onChange={e => setForm(f => ({ ...f, anschrift: e.target.value }))} />
              </FormGroup>
            </div>
            <div style={{ flex: "1 1 100%" }}>
              <FormGroup label="Grund der Einladung">
                <textarea className="form-control" rows={3} value={form.freitext}
                  placeholder="z. B. Sachverständiger für Brandschutz, Vertreter der Stadt…"
                  onChange={e => setForm(f => ({ ...f, freitext: e.target.value }))} />
              </FormGroup>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}


// ─── ABSTIMMUNG BLOCK ────────────────────────────────────────────────────────
function AbstimmungBlock({ top, sitzungId, reload }) {
  const [ja, setJa] = useState(top.abstimmung_ja ?? "");
  const [nein, setNein] = useState(top.abstimmung_nein ?? "");
  const [enthaltung, setEnthaltung] = useState(top.abstimmung_enthaltung ?? "");
  const [ergebnis, setErgebnis] = useState(top.abstimmung_ergebnis || null);
  const [saving, setSaving] = useState(false);

  // Sync wenn top sich von aussen ändert
  useEffect(() => {
    setJa(top.abstimmung_ja ?? "");
    setNein(top.abstimmung_nein ?? "");
    setEnthaltung(top.abstimmung_enthaltung ?? "");
    setErgebnis(top.abstimmung_ergebnis || null);
  }, [top.abstimmung_ja, top.abstimmung_nein, top.abstimmung_enthaltung, top.abstimmung_ergebnis]);

  const save = async () => {
    setSaving(true);
    try {
      const res = await api.setAbstimmung(sitzungId, top.id, {
        ja: parseInt(ja) || 0,
        nein: parseInt(nein) || 0,
        enthaltung: parseInt(enthaltung) || 0,
      });
      setErgebnis(res.abstimmung_ergebnis);
      setJa(res.abstimmung_ja ?? "");
      setNein(res.abstimmung_nein ?? "");
      setEnthaltung(res.abstimmung_enthaltung ?? "");
      reload();
    } finally { setSaving(false); }
  };

  return (
    <div style={{ marginTop: 12, background: "var(--paper)", border: "1px solid var(--border-strong)", padding: "10px 14px", borderRadius: 4 }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: "var(--ink-soft)", marginBottom: 8 }}>ABSTIMMUNG</div>
      <div style={{ display: "flex", gap: 12, marginBottom: 8, flexWrap: "wrap", alignItems: "center" }}>
        <label style={{ fontSize: 12, display: "flex", alignItems: "center", gap: 4 }}>
          Ja: <input type="number" min="0" value={ja}
            style={{ width: 60, padding: "2px 6px", border: "1px solid var(--border-strong)", borderRadius: 3 }}
            onChange={e => setJa(e.target.value)} />
        </label>
        <label style={{ fontSize: 12, display: "flex", alignItems: "center", gap: 4 }}>
          Nein: <input type="number" min="0" value={nein}
            style={{ width: 60, padding: "2px 6px", border: "1px solid var(--border-strong)", borderRadius: 3 }}
            onChange={e => setNein(e.target.value)} />
        </label>
        <label style={{ fontSize: 12, display: "flex", alignItems: "center", gap: 4 }}>
          Enthaltung: <input type="number" min="0" value={enthaltung}
            style={{ width: 60, padding: "2px 6px", border: "1px solid var(--border-strong)", borderRadius: 3 }}
            onChange={e => setEnthaltung(e.target.value)} />
        </label>
        <button className="btn btn-secondary btn-sm" onClick={save} disabled={saving}>
          {saving ? "…" : "Speichern"}
        </button>
      </div>
      {ergebnis && (
        <div style={{ fontSize: 12, fontWeight: 600, color: ergebnis === "angenommen" ? "var(--ok)" : "var(--danger)" }}>
          {ergebnis === "angenommen" ? "✓ Angenommen" : "✕ Abgelehnt"}
          <span style={{ fontWeight: 400, marginLeft: 8, color: "var(--ink-soft)" }}>
            (Ja: {ja} | Nein: {nein} | Enthaltung: {enthaltung})
          </span>
        </div>
      )}
    </div>
  );
}

// ─── ZUSAMMENFASSUNG TAB ─────────────────────────────────────────────────────
function ZusammenfassungTab({ sitzung, reload, setError, setSuccess }) {
  const _role = useGremiumRole(sitzung?.gremium_id);
  const _isAdmin = useIsAdmin();
  const canVorsitz = _isAdmin || roleSatisfies(_role, "vorsitz");
  const canSchriftfuehrer = _isAdmin || roleSatisfies(_role, "schriftfuehrer");
  const { info: meInfo } = useMeInfo();
  const myEmail = meInfo?.email || null;

  const [text, setText] = useState(sitzung.zusammenfassung || "");
  const [loading, setLoading] = useState(false);

  const save = async () => {
    try {
      await api.saveZusammenfassung(sitzung.id, text);
      setSuccess("Zusammenfassung gespeichert!");
      reload();
    } catch { setError("Fehler beim Speichern."); }
  };

  const generateKI = async () => {
    setLoading(true);
    try {
      // Kontext für KI aufbauen
      const tops = sitzung.tops || [];
      const beschluesse = tops.flatMap(t => t.beschluesse || []);

      const prompt = `Du bist Schriftführer einer Freiwilligen Feuerwehr. Erstelle eine kurze, sachliche Zusammenfassung der folgenden Sitzung auf Deutsch.

Sitzung: ${sitzung.titel}
Datum: ${dayjs(sitzung.datum).format("DD.MM.YYYY")}
Gremium: ${sitzung.gremium?.name}

Tagesordnung:
${tops.map(t => `- ${t.position}. ${t.titel} (${t.top_typ})${t.beschreibung ? ": " + t.beschreibung.slice(0, 100) : ""}`).join("\n")}

${beschluesse.length > 0 ? `Beschlüsse:\n${beschluesse.map(b => `- ${b.text}`).join("\n")}` : "Keine Beschlüsse."}

Schreibe eine prägnante Zusammenfassung in 3-5 Sätzen.`;

      const response = await fetch("https://api.anthropic.com/v1/messages", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          model: "claude-sonnet-4-20250514",
          max_tokens: 1000,
          messages: [{ role: "user", content: prompt }]
        })
      });

      const data = await response.json();
      const generiert = data.content?.[0]?.text || "";
      setText(generiert);
      setSuccess("KI-Zusammenfassung erstellt — bitte prüfen und speichern.");
    } catch { setError("Fehler bei der KI-Generierung."); }
    finally { setLoading(false); }
  };
  return (
    <div className={canSchriftfuehrer ? "grid-2" : ""}>
      {canSchriftfuehrer && (
      <div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <h3 style={{ fontSize: 15, fontWeight: 600 }}>Zusammenfassung</h3>
          <div style={{ display: "flex", gap: 8 }}>
            <button className="btn btn-secondary btn-sm" onClick={generateKI} disabled={loading}>
              {loading ? "⏳ Wird erstellt…" : "✨ KI-Zusammenfassung"}
            </button>
            <button className="btn btn-primary btn-sm" onClick={save}>Speichern</button>
          </div>
        </div>
        <textarea className="form-control" rows={15}
          placeholder="Zusammenfassung der Sitzung…"
          value={text}
          onChange={e => setText(e.target.value)}
          style={{ fontFamily: "inherit", fontSize: 13, lineHeight: 1.6 }} />
      </div>
      )}

      {/* Rechts: Vorschau */}
      <div className="card" style={{ height: "fit-content", position: "sticky", top: 20 }}>
        <div className="card-header"><h3>Vorschau</h3></div>
        <div className="card-body">
          {text ? (
            <div>
              <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 8, color: "var(--red)" }}>
                {sitzung.titel} — {dayjs(sitzung.datum).format("DD.MM.YYYY")}
              </div>
              <div style={{ fontSize: 13, lineHeight: 1.6, color: "var(--ink)", whiteSpace: "pre-wrap" }}>{text}</div>
            </div>
          ) : (
            <div className="empty-state">
              <div className="empty-icon">📝</div>
              <p>Noch keine Zusammenfassung</p>
              <p style={{ fontSize: 12, color: "var(--ink-faint)" }}>Manuell eingeben oder KI nutzen</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── DRINGLICHKEIT ZULASSUNG ─────────────────────────────────────────────────
function DringlichkeitZulassung({ antrag, sitzungId, reload }) {
  const [ja, setJa] = useState(antrag.zulassung_ja ?? "");
  const [nein, setNein] = useState(antrag.zulassung_nein ?? "");
  const [enthaltung, setEnthaltung] = useState(antrag.zulassung_enthaltung ?? "");
  const [ergebnis, setErgebnis] = useState(antrag.zulassung_ergebnis || null);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const res = await api.setZulassung(sitzungId, antrag.id, {
        ja: parseInt(ja) || 0,
        nein: parseInt(nein) || 0,
        enthaltung: parseInt(enthaltung) || 0
      });
      setErgebnis(res.zulassung_ergebnis);
      reload();
    } finally { setSaving(false); }
  };

  return (
    <div style={{ border: "1px solid var(--warn-bg)", borderRadius: 6, padding: "12px 16px", marginBottom: 12, background: "var(--warn-bg)" }}>
      <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 4 }}>⚡ {antrag.titel}</div>
      {antrag.beschreibung && <div style={{ fontSize: 12, color: "var(--ink-soft)", marginBottom: 8 }}>{antrag.beschreibung}</div>}
      <div style={{ fontSize: 11, fontWeight: 600, color: "var(--warn)", marginBottom: 8 }}>ABSTIMMUNG: Zulassung zur Tagesordnung</div>
      <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
        <label style={{ fontSize: 12, display: "flex", alignItems: "center", gap: 4 }}>
          Ja: <input type="number" min="0" value={ja}
            style={{ width: 60, padding: "2px 6px", border: "1px solid var(--border-strong)", borderRadius: 3 }}
            onChange={e => setJa(e.target.value)} />
        </label>
        <label style={{ fontSize: 12, display: "flex", alignItems: "center", gap: 4 }}>
          Nein: <input type="number" min="0" value={nein}
            style={{ width: 60, padding: "2px 6px", border: "1px solid var(--border-strong)", borderRadius: 3 }}
            onChange={e => setNein(e.target.value)} />
        </label>
        <label style={{ fontSize: 12, display: "flex", alignItems: "center", gap: 4 }}>
          Enthaltung: <input type="number" min="0" value={enthaltung}
            style={{ width: 60, padding: "2px 6px", border: "1px solid var(--border-strong)", borderRadius: 3 }}
            onChange={e => setEnthaltung(e.target.value)} />
        </label>
        <button className="btn btn-secondary btn-sm" onClick={save} disabled={saving}>
          {saving ? "…" : "Speichern"}
        </button>
      </div>
      {ergebnis && (
        <div style={{ marginTop: 8, fontSize: 12, fontWeight: 600, color: ergebnis === "angenommen" ? "var(--ok)" : "var(--danger)" }}>
          {ergebnis === "angenommen" ? "✓ Zugelassen — wird auf Tagesordnung aufgenommen" : "✕ Abgelehnt — wird nicht behandelt"}
          <span style={{ fontWeight: 400, marginLeft: 8, color: "var(--ink-soft)" }}>
            (Ja: {ja} | Nein: {nein} | Enthaltung: {enthaltung})
          </span>
        </div>
      )}
    </div>
  );
}

// ─── VERLAUF TAB ─────────────────────────────────────────────────────────────
function VerlaufTab({ sitzung }) {
  const _role = useGremiumRole(sitzung?.gremium_id);
  const _isAdmin = useIsAdmin();
  const canVorsitz = _isAdmin || roleSatisfies(_role, "vorsitz");
  const canSchriftfuehrer = _isAdmin || roleSatisfies(_role, "schriftfuehrer");
  const { info: meInfo } = useMeInfo();
  const myEmail = meInfo?.email || null;

  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.getAuditLog(sitzung.id)
      .then(setLogs)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [sitzung.id]);

  const aktionIcon = (aktion) => {
    if (aktion.includes('hinzugefügt')) return '➕';
    if (aktion.includes('gelöscht')) return '🗑';
    if (aktion.includes('angenommen')) return '✓';
    if (aktion.includes('abgelehnt')) return '✕';
    if (aktion.includes('zurückgezogen')) return '↩';
    if (aktion.includes('abgesagt')) return '✕';
    if (aktion.includes('Abstimmung')) return '🗳';
    if (aktion.includes('bearbeitet')) return '✎';
    return '•';
  };

  const aktionColor = (aktion) => {
    if (aktion.includes('hinzugefügt')) return 'var(--ok)';
    if (aktion.includes('gelöscht') || aktion.includes('abgesagt')) return 'var(--danger)';
    if (aktion.includes('angenommen')) return 'var(--ok)';
    if (aktion.includes('abgelehnt') || aktion.includes('zurückgezogen')) return 'var(--warn)';
    if (aktion.includes('Abstimmung')) return 'var(--info)';
    return 'var(--ink-soft)';
  };

  if (loading) return <div style={{ padding: 40, textAlign: "center" }}>Lädt…</div>;
  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <h3 style={{ fontSize: 15, fontWeight: 600 }}>Änderungsverlauf ({logs.length} Einträge)</h3>
        <button className="btn btn-secondary btn-sm" onClick={() => api.getAuditLog(sitzung.id).then(setLogs)}>
          ↻ Aktualisieren
        </button>
      </div>

      {logs.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">📋</div>
          <p>Noch keine Änderungen protokolliert</p>
        </div>
      ) : (
        <div style={{ position: "relative" }}>
          {/* Zeitlinie */}
          <div style={{ position: "absolute", left: 20, top: 0, bottom: 0, width: 2, background: "var(--border)" }} />

          {logs.map((log, i) => (
            <div key={log.id} style={{ display: "flex", gap: 16, marginBottom: 16, position: "relative" }}>
              {/* Icon */}
              <div style={{
                width: 40, height: 40, borderRadius: "50%", flexShrink: 0, zIndex: 1,
                background: "white", border: `2px solid ${aktionColor(log.aktion)}`,
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 16
              }}>
                {aktionIcon(log.aktion)}
              </div>

              {/* Inhalt */}
              <div className="card" style={{ flex: 1, marginBottom: 0 }}>
                <div style={{ padding: "10px 14px" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 4 }}>
                    <div style={{ fontWeight: 600, fontSize: 13, color: aktionColor(log.aktion) }}>
                      {log.aktion}
                    </div>
                    <div style={{ fontSize: 11, color: "var(--ink-faint)", flexShrink: 0, marginLeft: 8 }}>
                      {dayjs(log.created_at).format("DD.MM.YYYY HH:mm")} Uhr
                    </div>
                  </div>

                  {log.objekt_titel && (
                    <div style={{ fontSize: 13, marginBottom: 4 }}>
                      <span style={{ color: "var(--ink-soft)" }}>{log.objekt_typ}: </span>
                      <strong>{log.objekt_titel}</strong>
                    </div>
                  )}

                  {log.details && (
                    <div style={{ fontSize: 12, color: "var(--ink-soft)", background: "var(--paper)", padding: "4px 8px", borderRadius: 4, marginBottom: 4 }}>
                      {log.details}
                    </div>
                  )}

                  {(log.vorher || log.nachher) && (
                    <div style={{ display: "flex", gap: 8, fontSize: 11 }}>
                      {log.vorher && <span style={{ background: "var(--danger-bg)", color: "var(--danger)", padding: "2px 6px", borderRadius: 3 }}>vorher: {log.vorher}</span>}
                      {log.nachher && <span style={{ background: "var(--ok-bg)", color: "var(--ok)", padding: "2px 6px", borderRadius: 3 }}>nachher: {log.nachher}</span>}
                    </div>
                  )}

                  {(log.user_name || log.geraet || log.ip_adresse) && (
                    <div style={{ fontSize: 11, color: "var(--ink-faint)", marginTop: 4 }}>
                      {log.user_name && <span>👤 {log.user_name}</span>}
                      {log.geraet && <span style={{ marginLeft: log.user_name ? 8 : 0 }}>· 💻 {log.geraet}</span>}
                      {log.ip_adresse && <span style={{ marginLeft: 8 }}>· 🌐 {log.ip_adresse}</span>}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ProtokollVorschau({ sitzung }) {
  return (
    <div className="card">
      <div className="card-header"><h3>Vorschau</h3></div>
      <div className="card-body">
        {(sitzung.tops || []).map((top, i) => (
          <div key={top.id} style={{ marginBottom: 16, paddingBottom: 16, borderBottom: "1px solid var(--border)" }}>
            <div style={{ fontWeight: 600 }}>{i + 1}. {top.titel}</div>
            {top.notiz_protokoll ? (
              <div style={{ marginTop: 4, color: "var(--ink)", whiteSpace: "pre-wrap" }}>{top.notiz_protokoll}</div>
            ) : (
              <div style={{ marginTop: 4, color: "var(--ink-faint)", fontStyle: "italic" }}>— noch keine Notiz —</div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
